import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useProject } from '../../../context/ProjectContext';
import { useLocation, useParams } from 'react-router-dom';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import feasibilityService from '../../../services/feasibility.service';
import documentEditorService from '../../../services/documentEditor.service';
import studyProtocolService from '../../../services/studyProtocol.service';
import { generateContent } from '../../../services/AIService';
import agenticWorkflowService from '../../../services/agenticWorkflow.service';

const decodeHtmlEntities = (value = '') =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

const stripMarkup = (value = '', options = {}) => {
  if (typeof value !== 'string') return value;
  const { preserveLineBreaks = false } = options;
  let text = decodeHtmlEntities(value)
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/?[^>]+>/g, preserveLineBreaks ? '\n' : ' ')
    .replace(/&nbsp;/gi, ' ');

  if (preserveLineBreaks) {
    text = text
      .replace(/\r?\n[\s\t]*\r?\n/g, '\n\n')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{3,}/g, '\n\n');
  } else {
    text = text.replace(/\s+/g, ' ');
  }

  return text.trim();
};

const htmlToPlainText = (html = '') => {
  if (typeof html !== 'string') return html;
  let output = html;
  output = output.replace(/<h[1-6][^>]*>/gi, '\n\n');
  output = output.replace(/<\/h[1-6]>/gi, '\n');
  output = output.replace(/<p[^>]*>/gi, '\n\n');
  output = output.replace(/<li[^>]*>/gi, '\n• ');
  output = output.replace(/<br\s*\/?\s*>/gi, '\n');
  output = output.replace(/<table[^>]*>/gi, '\n\n');
  output = output.replace(/<\/table>/gi, '\n');
  output = output.replace(/<tr[^>]*>/gi, '\n');
  output = output.replace(/<\/tr>/gi, '\n');
  output = output.replace(/<th[^>]*>/gi, '\t');
  output = output.replace(/<td[^>]*>/gi, '\t');
  output = output.replace(/<\/th>/gi, '\t');
  output = output.replace(/<\/td>/gi, '\t');
  output = stripMarkup(output, { preserveLineBreaks: true });
  output = output.replace(/\t+/g, '\t').replace(/\n{3,}/g, '\n\n');
  return output.trim();
};

const findJsonBlock = (text = '') => {
  if (typeof text !== 'string') return null;
  const targets = ['"inputs_summary"', '"countries"', '"weights_used"'];
  let start = -1;
  let depth = 0;
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (start === -1) {
      if (char === '{') {
        start = i;
        depth = 1;
        inString = false;
        escapeNext = false;
      }
      continue;
    }

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      if (inString) {
        escapeNext = true;
      }
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) {
      continue;
    }

    if (char === '{') {
      depth += 1;
      continue;
    }

    if (char === '}') {
      depth -= 1;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1);
        if (targets.some((target) => candidate.includes(target))) {
          return candidate;
        }
        start = -1;
        depth = 0;
        inString = false;
        escapeNext = false;
      }
    }
  }

  return null;
};

const extractJsonFromResponse = (text) => {
  if (typeof text !== 'string') return { cleanedText: '', json: null };
  let workingText = decodeHtmlEntities(text.trim());
  let json = null;

  const parseCandidate = (candidate) => {
    if (!candidate) return null;
    try {
      return JSON.parse(decodeHtmlEntities(candidate));
    } catch (err) {
      return null;
    }
  };

  // Try multiple patterns to find JSON
  // 1. Try markdown code blocks first (with or without json label)
  const tripleBacktickMatch = workingText.match(/```(?:json)?[\s\S]*?```/i);
  if (tripleBacktickMatch) {
    const jsonCandidate = tripleBacktickMatch[0]
      .replace(/```(?:json)?/i, '')
      .replace(/```/g, '')
      .trim();
    json = parseCandidate(jsonCandidate);
    if (json) {
      workingText = workingText.replace(tripleBacktickMatch[0], '').trim();
      return { cleanedText: workingText, json };
    }
  }

  // 2. Try finding JSON block by braces
  const braceCandidate = findJsonBlock(workingText);
  if (braceCandidate) {
    json = parseCandidate(braceCandidate);
    if (json) {
      workingText = workingText.replace(braceCandidate, '').trim();
      return { cleanedText: workingText, json };
    }
  }

  // 3. Try finding JSON at the end of the text (common pattern for feasibility reports)
  const endJsonMatch = workingText.match(/\{[\s\S]*"inputs_summary"[\s\S]*\}$/);
  if (endJsonMatch) {
    json = parseCandidate(endJsonMatch[0]);
    if (json) {
      workingText = workingText.replace(endJsonMatch[0], '').trim();
      return { cleanedText: workingText, json };
    }
  }

  return { cleanedText: workingText, json: null };
};

const requestStructuredJson = async ({ narrative, placeholders, questionSummary }) => {
  const baseContext = Object.entries(placeholders)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
    .join('\n');

  const prompt = `You are a clinical operations feasibility analyst. A narrative feasibility report has been generated but the structured JSON summary is missing.\n\nYour task: produce ONLY the JSON block specified below, populated with the best available data. Use the narrative content and the contextual inputs provided.\n\nNarrative:\n${narrative}\n\nContext placeholders:\n${baseContext}\n\nFeasibility questionnaire Q&A:\n${questionSummary}\n\nOutput requirements:\n- Return STRICTLY valid JSON matching this schema (no comments, no extra text):\n{\n  "inputs_summary": {},\n  "weights_used": {\n    "incidence": number,\n    "prior_therapy": number,\n    "biomarker": number,\n    "financial": number,\n    "timeline": number,\n    "regulatory": number,\n    "competition_historical": number,\n    "sponsor_context": number\n  },\n  "countries": [\n    {\n      "country": string,\n      "metrics": {\n        "incidence": { "asr_per_100k": number, "annual_incident": number, "eligible_y": number, "eligible_m": number, "assumptions": string[] },\n        "prior_therapy": { "pct_meeting_IE": number, "alignment": string, "notes": string },\n        "biomarker": { "positivity_pct": number, "testing_coverage_pct": number, "capacity_note": string, "tat_days": number, "readiness_index": number },\n        "financial": { "startup_usd": number, "fixed_per_site_usd": number, "variable_per_patient_usd": number, "expected_rpm": number, "cprp_usd": number, "budget_share_pct": number, "cost_risks": string },\n        "timeline": { "contracting_days": number, "ec_irb_days": number, "authority_days": number, "import_days": number, "startup_total_days": number, "time_to_fpi_days": number, "activation_risk": string },\n        "regulatory": { "framework": string, "key_requirements": string, "parallelization": string, "complexity_score": number, "mitigations": string },\n        "competition_historical": { "active_competing_trials": number, "competition_index": number, "historical_site_rpm": number, "historical_startup_days": number, "data_quality_notes": string },\n        "sponsor_context": { "affiliate": string, "site_relationships": string, "cro_coverage": string, "leverage_opportunities": string }\n      },\n      "composite_score": number,\n      "proposed_sites": number,\n      "projected_rpm": number,\n      "projected_contribution_to_N": number,\n      "role": string,\n      "key_risks": string,\n      "citations": number[]\n    }\n  ],\n  "recommended_portfolio": { "must_have": string[], "optional": string[], "backup": string[] },\n  "feasibility_check": { "target_n": number, "enrollment_months": number, "projected_total_randomizations": number, "meets_target": boolean },\n  "assumptions": string[],\n  "sensitivity": [ { "scenario": string, "projected_total_randomizations": number, "meets_target": boolean } ]\n}\n\nIf a value is unavailable, use a conservative numeric estimate (0 if truly unknown) and include an explanatory string in notes/assumptions instead of leaving null. Ensure numeric fields are numbers (not strings).`;

  const response = await generateContent(
    prompt,
    '',
    '',
    'feasibility',
    'Country Feasibility JSON'
  );

  return response;
};

const tableIdMap = {
  A1: /Table\s*A1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[B-Z]\d|\n\d+\.\s|$)/i,
  B1: /Table\s*B1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[C-Z]\d|\n\d+\.\s|$)/i,
  C1: /Table\s*C1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[D-Z]\d|\n\d+\.\s|$)/i,
  D1: /Table\s*D1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[E-Z]\d|\n\d+\.\s|$)/i,
  E1: /Table\s*E1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[F-Z]\d|\n\d+\.\s|$)/i,
  F1: /Table\s*F1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[G-Z]\d|\n\d+\.\s|$)/i,
  G1: /Table\s*G1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[H-Z]\d|\n\d+\.\s|$)/i,
  H1: /Table\s*H1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[I-Z]\d|\n\d+\.\s|$)/i,
  I1: /Table\s*I1[^\n]*\n([\s\S]*?)(?=\n\s*Table\s*[J-Z]\d|\n\d+\.\s|$)/i,
  // For J1 (last table), capture everything until JSON block starts or end of string
  // Use a more permissive pattern to ensure we get all content
  J1: /Table\s*J1[^\n]*\n([\s\S]*?)(?=\s*\{[\s\S]*"inputs_summary"|\s*\{[\s\S]*"weights_used"|$)/i,
};

const extractFallbackTables = (narrative = '') => {
  const results = {};
  Object.entries(tableIdMap).forEach(([tableId, regex]) => {
    const match = narrative.match(regex);
    if (match && match[1]) {
      results[tableId] = { type: 'text', content: match[1].trim() };
    } else if (tableId === 'J1') {
      // Special handling for J1 (last table) - try to find it even if incomplete
      const j1Match = narrative.match(/Table\s*J1[^\n]*\n([\s\S]*)/i);
      if (j1Match && j1Match[1]) {
        // Extract everything after Table J1 until the end or JSON block
        let content = j1Match[1].trim();
        // Remove JSON block if present at the end
        const jsonStart = content.search(/\s*\{[\s\S]*"inputs_summary"/);
        if (jsonStart > 0) {
          content = content.substring(0, jsonStart).trim();
        }
        if (content.length > 0) {
          results[tableId] = { type: 'text', content };
        }
      }
    }
  });
  return results;
};

const guessTableIdFromTitle = (title = '') => {
  const match = title.match(/Table\s*([A-Z]\d)/i);
  return match ? match[1].toUpperCase() : null;
};

const parseHtmlTables = (html = '') => {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const results = {};

  const tables = doc.querySelectorAll('table');
  tables.forEach((table) => {
    const caption = table.querySelector('caption');
    const captionText = caption ? caption.textContent.trim() : '';
    let inferredId = guessTableIdFromTitle(captionText);

    if (!inferredId) {
      let prev = table.previousElementSibling;
      while (prev && !inferredId) {
        const text = prev.textContent.trim();
        inferredId = guessTableIdFromTitle(text);
        prev = prev.previousElementSibling;
      }
    }

    if (!inferredId) return;

    const headers = Array.from(table.querySelectorAll('thead tr'))
      .map((tr) => Array.from(tr.querySelectorAll('th,td')).map((cell) => cell.textContent.trim()))
      .flat();

    const bodyRows = Array.from(table.querySelectorAll('tbody tr')).map((tr) =>
      Array.from(tr.querySelectorAll('th,td')).map((cell) => cell.textContent.trim())
    );

    if (!headers.length && bodyRows.length) {
      const firstRow = bodyRows[0];
      headers.push(...firstRow);
      bodyRows.shift();
    }

    if (!headers.length) return;

    results[inferredId] = {
      type: 'table',
      title: captionText || `Table ${inferredId}`,
      headers,
      rows: bodyRows,
    };
  });

  return results;
};

const getNarrativeSummary = (text = '') => {
  if (!text) return '';
  const firstTableIndex = text.search(/\bTable\s+[A-Z]\d/i);
  let summary = firstTableIndex > 0 ? text.slice(0, firstTableIndex) : text;
  summary = summary.trim();
  if (summary.length > 1500) {
    summary = `${summary.slice(0, 1500).trim()}…`;
  }
  return summary;
};

const CountryFeasibility = () => {
  const { currentProject, loadProject } = useProject();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectIdFromUrl = queryParams.get('projectId') || queryParams.get('project');
  const { id: projectIdFromParams } = useParams();
  // Get projectId from URL params or context
  const projectId = projectIdFromUrl || projectIdFromParams || currentProject?._id;
  const [reportText, setReportText] = useState('');
  const [reportJson, setReportJson] = useState(null);
  const [fallbackTables, setFallbackTables] = useState({});
  const [usedFallbackTables, setUsedFallbackTables] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [summaryNote, setSummaryNote] = useState('');
  const [saveAlert, setSaveAlert] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [countrySelectionSaving, setCountrySelectionSaving] = useState(false);
  const [countrySelectionError, setCountrySelectionError] = useState(null);
  const [countrySelectionSuccess, setCountrySelectionSuccess] = useState(null);
  
  // Agentic workflow state
  const [useAgenticWorkflow, setUseAgenticWorkflow] = useState(true);
  const [workflowProgress, setWorkflowProgress] = useState(0);
  const [workflowStep, setWorkflowStep] = useState('');
  const [workflowStatus, setWorkflowStatus] = useState('idle'); // idle, running, completed, failed
  const [agentResults, setAgentResults] = useState(null);
  const [citationsData, setCitationsData] = useState([]);

  // PostgreSQL data state
  const [postgresData, setPostgresData] = useState([]);
  const [postgresSearchTerms, setPostgresSearchTerms] = useState([]);
  const [postgresLoading, setPostgresLoading] = useState(false);
  const [postgresError, setPostgresError] = useState(null);
  const [countryStudyCountsFromApi, setCountryStudyCountsFromApi] = useState(null);
  
  // Pagination state for PostgreSQL data table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Section 5 Trial Population Summary state
  const [trialPopulationSummary, setTrialPopulationSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [processingSummary, setProcessingSummary] = useState(false);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  
  // Modal state for showing NCT IDs
  const [nctIdModalOpen, setNctIdModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCountryNctIds, setSelectedCountryNctIds] = useState([]);
  
  // Map tooltip state
  const [mapTooltipContent, setMapTooltipContent] = useState('');
  const [mapTooltipPosition, setMapTooltipPosition] = useState({ x: 0, y: 0 });
  const [showMapTooltip, setShowMapTooltip] = useState(false);

  // Eligibility Embeddings Search state
  const [eligibilitySearchQuery, setEligibilitySearchQuery] = useState('');
  const [eligibilitySearchResults, setEligibilitySearchResults] = useState(null);
  const [eligibilitySearchLoading, setEligibilitySearchLoading] = useState(false);
  const [eligibilitySearchError, setEligibilitySearchError] = useState(null);
  const [embeddingGenerationStatus, setEmbeddingGenerationStatus] = useState(null); // 'idle', 'generating', 'success', 'error'
  const [embeddingGenerationProgress, setEmbeddingGenerationProgress] = useState(null);
  const [requiresEmbeddingGeneration, setRequiresEmbeddingGeneration] = useState(false);

    // Regenerate MeSH terms state
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  
  // Queue-based similarity search state
  const [similarityJobId, setSimilarityJobId] = useState(null);
  const [similarityJobStatus, setSimilarityJobStatus] = useState(null); // 'queued', 'processing', 'completed', 'failed'
  const [similarityJobProgress, setSimilarityJobProgress] = useState(0);
  const [similarityJobMessage, setSimilarityJobMessage] = useState('');
  const [similarityJobError, setSimilarityJobError] = useState(null);

  const countriesForTables = useMemo(() => {
    if (!reportJson?.countries || !reportJson.countries.length) return [];
    // return reportJson.countries
    return reportJson.countries
      .slice()
      .sort((a, b) => (a.country || '').localeCompare(b.country || ''));
  //}, [reportJson]);
  }, [reportJson]);

  const allGeneratedCountries = useMemo(() => {
    const seen = new Set();
    const ordered = [];

    if (reportJson?.countries && Array.isArray(reportJson.countries)) {
      reportJson.countries.forEach((country) => {
        const name = country?.country ? String(country.country).trim() : '';
        if (name && !seen.has(name)) {
          seen.add(name);
          ordered.push(name);
        }
      });
    }

    const tableIds = ['I1', 'A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'H1', 'J1'];
    if (fallbackTables && typeof fallbackTables === 'object') {
      tableIds.forEach((tableId) => {
        const table = fallbackTables[tableId];
        const rows = table?.data?.rows;
        if (!Array.isArray(rows)) return;
        rows.forEach((row) => {
          const value = row?.Country ?? row?.country ?? row?.COUNTRY;
          const name = value ? String(value).trim() : '';
          if (name && !seen.has(name)) {
            seen.add(name);
            ordered.push(name);
          }
        });
      });
    }

    return ordered;
  }, [fallbackTables, reportJson]);

  const effectiveProjectId = useMemo(
    () => currentProject?._id || projectIdFromUrl || projectIdFromParams || '',
    [currentProject, projectIdFromParams, projectIdFromUrl]
  );

  const protocolNumber = useMemo(() => {
    if (currentProject) {
      return (
        currentProject.protocolNumber ||
        currentProject?.protocol?.number ||
        currentProject?.protocolId ||
        currentProject.protocol?.protocolNumber ||
        ''
      );
    }
    return '';
  }, [currentProject]);

  useEffect(() => {
    if (!currentProject && effectiveProjectId) {
      loadProject(effectiveProjectId);
    }
  }, [currentProject, effectiveProjectId, loadProject]);

  useEffect(() => {
    if (!effectiveProjectId) return;
    let isMounted = true;

    const loadCountryReport = async () => {
      try {
        const data = await feasibilityService.getCountryReport(effectiveProjectId);
        if (!isMounted || !data) return;
        setReportText(data.reportText || '');
        setReportJson(data.reportJson || null);
        setSummaryNote(data.summaryNote || '');
        setFallbackTables(data.fallbackTables || {});
        setUsedFallbackTables(Boolean(data.usedFallbackTables));
        setCitationsData(data.citations || []);
        setSelectedCountries(Array.isArray(data.selectedCountries) ? data.selectedCountries : []);
      } catch (loadErr) {
        console.warn('Failed to load saved country feasibility report:', loadErr);
      }
    };

    loadCountryReport();

    return () => {
      isMounted = false;
    };
  }, [effectiveProjectId]);

  useEffect(() => {
    if (!allGeneratedCountries.length) return;
    if (selectedCountries && selectedCountries.length) return;
    setSelectedCountries(allGeneratedCountries);
  }, [allGeneratedCountries, selectedCountries]);

  const toggleSelectedCountry = (countryName) => {
    const key = String(countryName || '').trim();
    if (!key) return;
    setSelectedCountries((prev) => {
      const set = new Set(Array.isArray(prev) ? prev : []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    });
  };

  const handleSaveCountrySelection = async () => {
    if (!effectiveProjectId) {
      setCountrySelectionError('Project ID is required to save selected countries.');
      return;
    }
    if (!selectedCountries || selectedCountries.length === 0) {
      setCountrySelectionError('Select at least one country to continue.');
      return;
    }

    setCountrySelectionSaving(true);
    setCountrySelectionError(null);
    setCountrySelectionSuccess(null);

    try {
      const saved = await feasibilityService.saveCountryReport(effectiveProjectId, {
        reportText,
        reportJson,
        summaryNote,
        fallbackTables,
        usedFallbackTables,
        citations: citationsData,
        selectedCountries,
      });
      setCountrySelectionSuccess('Selected countries saved.');
      if (saved?.selectedCountries) {
        setSelectedCountries(Array.isArray(saved.selectedCountries) ? saved.selectedCountries : selectedCountries);
      }
    } catch (saveErr) {
      console.error('Failed to save selected countries:', saveErr);
      setCountrySelectionError(
        saveErr?.response?.data?.message || saveErr?.message || 'Failed to save selected countries.'
      );
    } finally {
      setCountrySelectionSaving(false);
    }
  };

  const extractProtocolCriteria = async () => {
    let protocolPayload = null;
    let inclusionText = '';
    let exclusionText = '';
    let extractedFields = {
      indication: null,
      subtype: null,
      studyDesign: null,
      targetSampleSize: null,
      enrollmentMonths: null,
      biomarkerList: null,
      mustIncludeCountries: null,
      excludeCountries: null,
      preferredCountries: null,
      affiliateMap: null,
      kolNotes: null
    };

    // Helper to extract text from sections by title keywords
    const extractFromSections = (sectionsInput = [], keywords = []) => {
      if (!Array.isArray(sectionsInput)) return '';
      const matchingSections = sectionsInput.filter((section) => {
        const title = (section.title || '').toLowerCase();
        return keywords.some(keyword => title.includes(keyword.toLowerCase()));
      });
      return matchingSections
        .map((section) => stripMarkup(section.output || section.context || ''))
        .filter(Boolean)
        .join('\n');
    };

    // Helper to extract structured data from protocol model
    const extractStructuredFields = (protocol) => {
      if (!protocol) return {};
      
      const fields = {};
      
      // Extract from study_overview
      if (protocol.study_overview) {
        fields.indication = protocol.study_overview.disease_indication || null;
        fields.subtype = protocol.study_overview.therapeutic_area || null;
        
        // Build study design from multiple fields
        const designParts = [];
        if (protocol.study_overview.study_phase) designParts.push(protocol.study_overview.study_phase);
        if (protocol.study_overview.study_type) designParts.push(protocol.study_overview.study_type);
        if (protocol.study_overview.trial_intervention_model) designParts.push(protocol.study_overview.trial_intervention_model);
        if (protocol.study_overview.control_method) designParts.push(`Control: ${protocol.study_overview.control_method}`);
        if (protocol.study_overview.blinding) designParts.push(protocol.study_overview.blinding);
        if (protocol.study_overview.randomization) designParts.push('Randomized');
        fields.studyDesign = designParts.length > 0 ? designParts.join(', ') : null;
      }
      
      // Extract from target_population
      if (protocol.target_population) {
        if (protocol.target_population.key_inclusion_criteria && Array.isArray(protocol.target_population.key_inclusion_criteria)) {
          inclusionText = protocol.target_population.key_inclusion_criteria.join('\n');
        }
        if (protocol.target_population.key_exclusion_criteria && Array.isArray(protocol.target_population.key_exclusion_criteria)) {
          exclusionText = protocol.target_population.key_exclusion_criteria.join('\n');
        }
      }
      
      // Extract from study_identification
      if (protocol.study_identification) {
        // Protocol title might contain indication info
        if (!fields.indication && protocol.study_identification.study_title) {
          // Try to extract indication from title
          const title = protocol.study_identification.study_title;
          fields.indication = title; // Use title as fallback
        }
      }
      
      return fields;
    };

    // Helper to parse sections for specific fields
    const parseSectionsForFields = (sections = []) => {
      const parsed = {};
      
      sections.forEach(section => {
        const title = (section.title || '').toLowerCase();
        const content = stripMarkup(section.output || section.context || '');
        
        // Extract indication
        if (!parsed.indication && (title.includes('indication') || title.includes('disease') || title.includes('condition'))) {
          parsed.indication = content.substring(0, 500);
        }
        
        // Extract study design
        if (!parsed.studyDesign && (title.includes('design') || title.includes('overview') || title.includes('summary'))) {
          parsed.studyDesign = content.substring(0, 500);
        }
        
        // Extract sample size
        if (!parsed.targetSampleSize && (title.includes('sample') || title.includes('size') || title.includes('enrollment'))) {
          const sizeMatch = content.match(/(?:sample size|n\s*=|enrollment|randomiz)\D*(\d+)/i);
          if (sizeMatch) parsed.targetSampleSize = sizeMatch[1];
        }
        
        // Extract enrollment duration
        if (!parsed.enrollmentMonths && (title.includes('enrollment') || title.includes('duration') || title.includes('timeline'))) {
          const monthMatch = content.match(/(?:enrollment|duration|months?)\D*(\d+)\s*(?:month|mon)/i);
          if (monthMatch) parsed.enrollmentMonths = monthMatch[1];
        }
        
        // Extract biomarkers
        if (!parsed.biomarkerList && (title.includes('biomarker') || title.includes('marker') || title.includes('genetic'))) {
          parsed.biomarkerList = content.substring(0, 500);
        }
        
        // Extract countries
        if (!parsed.mustIncludeCountries && (title.includes('country') || title.includes('geographic') || title.includes('region'))) {
          const countryMatch = content.match(/(?:countries?|regions?)[:\s]+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
          if (countryMatch) parsed.mustIncludeCountries = countryMatch[1];
        }
      });
      
      return parsed;
    };

    // Try to get protocol from studyProtocolService first
    if (protocolNumber) {
      try {
        protocolPayload = await studyProtocolService.getStudyProtocolByNumber(protocolNumber);
        
        // Extract structured fields from protocol model
        const structuredFields = extractStructuredFields(protocolPayload);
        Object.assign(extractedFields, structuredFields);
        
        // Extract inclusion/exclusion from protocol model
        if (protocolPayload?.sections) {
          const sectionInclusion = extractFromSections(protocolPayload.sections, ['inclusion']);
          const sectionExclusion = extractFromSections(protocolPayload.sections, ['exclusion']);
          if (sectionInclusion) inclusionText = sectionInclusion;
          if (sectionExclusion) exclusionText = sectionExclusion;
        }
        
        // Fallback to direct fields if available
        if (!inclusionText && protocolPayload?.inclusionCriteria) {
          inclusionText = stripMarkup(protocolPayload.inclusionCriteria);
        }
        if (!exclusionText && protocolPayload?.exclusionCriteria) {
          exclusionText = stripMarkup(protocolPayload.exclusionCriteria);
        }
        
        // Parse sections for additional fields
        if (protocolPayload?.sections) {
          const parsedFields = parseSectionsForFields(protocolPayload.sections);
          Object.assign(extractedFields, parsedFields);
        }
      } catch (err) {
        console.warn('Protocol lookup by number failed, will attempt project sections.', err);
      }
    }

    // Fallback to document sections if needed
    if ((!protocolPayload || !inclusionText || !exclusionText) && effectiveProjectId) {
      try {
        const protocolSections = await documentEditorService.getAllSections(effectiveProjectId);
        if (Array.isArray(protocolSections) && protocolSections.length) {
          const filteredSections = protocolSections
            .filter(
              (section) =>
                (section.documentType ? section.documentType === 'PROTOCOL' : true) &&
                (section.output || section.context)
            )
            .map((section) => ({
              title: section.title,
              number: section.number || section.order || null,
              output: stripMarkup(section.output || ''),
              context: stripMarkup(section.context || ''),
            }));
          
          protocolPayload = {
            projectId: effectiveProjectId,
            sections: filteredSections
          };
          
          // Extract from sections
          const sectionInclusion = extractFromSections(filteredSections, ['inclusion']);
          const sectionExclusion = extractFromSections(filteredSections, ['exclusion']);
          if (sectionInclusion) inclusionText = sectionInclusion;
          if (sectionExclusion) exclusionText = sectionExclusion;
          
          // Parse sections for all fields
          const parsedFields = parseSectionsForFields(filteredSections);
          Object.assign(extractedFields, parsedFields);
        }
      } catch (sectionsError) {
        console.error('Fallback to document sections failed.', sectionsError);
      }
    }

    // Merge extracted fields into protocolPayload for easy access
    if (protocolPayload) {
      protocolPayload.extractedFields = extractedFields;
    }

    return { inclusionText, exclusionText, protocolPayload, extractedFields };
  };

  /**
   * Handle generation using agentic workflow
   */
  const handleAgenticWorkflowGeneration = async () => {
    try {
      const { inclusionText, exclusionText, protocolPayload, extractedFields } = await extractProtocolCriteria();

      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      // Extract from protocol model structure
      const studyTitle = currentProject?.title || currentProject?.name || protocolPayload?.study_identification?.study_title || 'Not specified';
      const studyPhase = protocolPayload?.study_overview?.study_phase || 'Not specified';
      const studyType = protocolPayload?.study_overview?.study_type || 'Not specified';
      const sampleSize = protocolPayload?.study_design_details?.sample_size_total || extractedFields?.targetSampleSize || null;
      const countriesFromProtocol = protocolPayload?.site_operations?.site_activation?.countries_involved || 
                                   protocolPayload?.regulatory_ethical_requirements?.countries_for_submission?.join(', ') || null;
      
      // Build placeholders using extracted fields and protocol model structure
      const placeholders = {
        today_date: today,
        indication: extractedFields?.indication || 
                   protocolPayload?.study_overview?.disease_indication || 
                   studyTitle || 
                   'Not specified',
        subtype_or_line_of_therapy: extractedFields?.subtype || 
                                   protocolPayload?.study_overview?.therapeutic_area || 
                                   'Not specified',
        study_design_overview: extractedFields?.studyDesign || 
                             (protocolPayload?.study_overview ? 
                               `${studyPhase}, ${studyType}, ${protocolPayload.study_overview.trial_intervention_model || ''}, ${protocolPayload.study_overview.control_method || ''}, ${protocolPayload.study_overview.blinding || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '') :
                               studyType) || 
                             'Not specified',
        target_sample_size: sampleSize || 
                           extractedFields?.targetSampleSize || 
                           'Not specified',
        enrollment_months: extractedFields?.enrollmentMonths || 
                          protocolPayload?.study_design_details?.study_duration?.treatment_period || 
                          'Not specified',
        inclusion_criteria: inclusionText || 
                          (protocolPayload?.target_population?.key_inclusion_criteria ? 
                            protocolPayload.target_population.key_inclusion_criteria.join('\n') : 
                            'Not specified'),
        exclusion_criteria: exclusionText || 
                          (protocolPayload?.target_population?.key_exclusion_criteria ? 
                            protocolPayload.target_population.key_exclusion_criteria.join('\n') : 
                            'Not specified'),
        biomarker_list: extractedFields?.biomarkerList || 
                       protocolPayload?.study_assessments?.laboratory_tests || 
                       'Not specified',
        must_include: extractedFields?.mustIncludeCountries || 
                     countriesFromProtocol || 
                     'Not specified',
        exclude: extractedFields?.excludeCountries || 
                protocolPayload?.excludeCountries || 
                'None',
        preferred_countries: extractedFields?.preferredCountries || 
                           protocolPayload?.preferredCountries || 
                           'Not specified',
        affiliate_map: extractedFields?.affiliateMap || 
                      protocolPayload?.affiliateMap || 
                      'Not specified',
        kol_notes: extractedFields?.kolNotes || 
                  protocolPayload?.kolNotes || 
                  'Not specified',
        has_internal_perf_data: 'Not specified',
        assumption_overrides: 'None',
      };

      // Debug: Log extracted protocol data
      console.log('📋 [Country Feasibility] Protocol Data Extracted:', {
        hasProtocolPayload: !!protocolPayload,
        extractedFields: extractedFields,
        inclusionLength: inclusionText?.length || 0,
        exclusionLength: exclusionText?.length || 0,
        placeholders: {
          indication: placeholders.indication,
          studyDesign: placeholders.study_design_overview,
          sampleSize: placeholders.target_sample_size,
          enrollmentMonths: placeholders.enrollment_months,
          biomarkers: placeholders.biomarker_list,
          countries: placeholders.must_include
        }
      });

      // Execute agentic workflow
      const results = await agenticWorkflowService.generateCountryFeasibility(
        {
          placeholders,
          protocolData: protocolPayload,
          requirements: {
            tables: ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'H1', 'I1', 'J1'],
            sections: [
              'Executive Summary',
              'Incidence Analysis',
              'Prior Therapy Assessment',
              'Biomarker Feasibility',
              'Financial Analysis',
              'Timeline Assessment',
              'Regulatory Overview',
              'Competition Analysis',
              'Portfolio Recommendation'
            ]
          }
        },
        (progress) => {
          // Progress callback
          setWorkflowProgress(progress.progress);
          setWorkflowStep(progress.message);
        }
      );

      // Set results
      setReportText(results.reportText);
      setReportJson(results.reportJson);
      setAgentResults(results);
      setCitationsData(results.citations || []);
      setWorkflowStatus('completed');

      // CRITICAL FIX: Map tables from agentic workflow to fallbackTables for UI rendering
      console.log('🔍 [Frontend] Received results:', {
        hasTables: !!results.tables,
        tableCount: results.tables ? Object.keys(results.tables).length : 0,
        tableIds: results.tables ? Object.keys(results.tables) : []
      });
      
      if (results.tables) {
        const convertedTables = {};
        
        Object.entries(results.tables).forEach(([tableId, tableData]) => {
          console.log(`🔍 [Frontend] Processing table ${tableId}:`, {
            hasData: !!tableData?.data,
            hasRows: !!tableData?.data?.rows,
            isArray: Array.isArray(tableData?.data?.rows),
            rowCount: tableData?.data?.rows?.length || 0,
            hasColumns: !!tableData?.columns,
            columnCount: tableData?.columns?.length || 0
          });
          
          if (tableData?.data?.rows && Array.isArray(tableData.data.rows)) {
            // Check if rows have data
            if (tableData.data.rows.length === 0) {
              console.warn(`⚠️ [Frontend] Table ${tableId} has 0 rows!`);
            } else {
              console.log(`✓ [Frontend] Table ${tableId} has ${tableData.data.rows.length} rows`);
            }
            
            // Convert agentic workflow table format to fallback format
            const headers = tableData.columns || Object.keys(tableData.data.rows[0] || {});
            const rows = tableData.data.rows.map(rowObj => 
              headers.map(col => rowObj[col] || '—')
            );
            
            console.log(`✓ [Frontend] Converted table ${tableId}:`, {
              headers: headers.length,
              rows: rows.length,
              sampleRow: rows[0]
            });
            
            convertedTables[tableId] = {
              type: 'table',
              title: tableData.name,
              headers: headers,
              rows: rows
            };
          } else {
            console.error(`❌ [Frontend] Table ${tableId} missing data.rows or not array:`, {
              tableData: tableData ? {
                hasData: !!tableData.data,
                dataType: typeof tableData.data,
                dataKeys: tableData.data ? Object.keys(tableData.data) : null
              } : 'null'
            });
          }
        });
        
        console.log('✓ [Frontend] Final converted tables:', Object.keys(convertedTables));
        setFallbackTables(convertedTables);
        setUsedFallbackTables(true);
      } else {
        console.error('❌ [Frontend] No tables in results!');
      }

      // Generate summary note
      const evaluationScore = results.evaluation?.overallScore || 'N/A';
      const issuesCount = results.evaluation?.issues?.length || 0;
      
      setSummaryNote(
        [
          `✓ Country feasibility report generated successfully`,
          ``,
          `**Report Contents:**`,
          `- ${results.metadata?.generationAgent?.tablesGenerated || 10} comprehensive analysis tables`,
          `- ${results.metadata?.searchQueries?.total || 0} data sources searched`,
          `- ${results.citations?.length || 0} unique references cited`,
          `- Quality score: ${evaluationScore}/100${issuesCount > 0 ? ` (${issuesCount} issues to review)` : ''}`,
          ``,
          `**Data Sources:**`,
          `- Protocol specifications and criteria`,
          `- Real-world epidemiology and incidence data`,
          `- Regulatory requirements and timelines`,
          `- Financial benchmarks and cost estimates`,
          `- Competitive landscape analysis`,
          ``,
          `**Next Steps:**`,
          `- Review generated tables for accuracy`,
          `- Check data sources in References section`,
          `- Verify recommendations align with protocol`,
          `- Export report when satisfied with results`
        ].filter(Boolean).join('\n')
      );

    } catch (error) {
      console.error('Agentic workflow error:', error);
      setWorkflowStatus('failed');
      throw error;
    }
  };

  const handleGenerate = async () => {
    if (!effectiveProjectId) {
      setError('Please select a project first.');
      return;
    }

    setLoading(true);
    setError(null);
    setReportText('');
    setReportJson(null);
    setFallbackTables({});
    setUsedFallbackTables(false);
    setSummaryNote('');
    setSaveAlert(null);
    setWorkflowProgress(0);
    setWorkflowStep('Initializing...');
    setWorkflowStatus('running');
    setAgentResults(null);
    setCitationsData([]);

    try {
      // Check if we should use agentic workflow
      if (useAgenticWorkflow) {
        await handleAgenticWorkflowGeneration();
        return;
      }
      
      // Otherwise continue with traditional generation
      const { inclusionText, exclusionText, protocolPayload } = await extractProtocolCriteria();

      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      // Extract study information from protocol payload if available
      const studyTitle = currentProject?.title || currentProject?.name || protocolPayload?.title || 'Not specified';
      const studyType = protocolPayload?.studyType || protocolPayload?.phase || 'Not specified';
      
      const placeholders = {
        today_date: today,
        indication: protocolPayload?.indication || protocolPayload?.diseaseIndication || studyTitle || 'Not specified',
        subtype_or_line_of_therapy: protocolPayload?.subtype || protocolPayload?.lineOfTherapy || 'Not specified',
        study_design_overview: protocolPayload?.studyDesign || studyType || 'Not specified',
        target_sample_size: protocolPayload?.targetSampleSize || protocolPayload?.sampleSize || 'Not specified',
        enrollment_months: protocolPayload?.enrollmentMonths || protocolPayload?.enrollmentDuration || 'Not specified',
        inclusion_criteria: inclusionText || 'Not specified',
        exclusion_criteria: exclusionText || 'Not specified',
        biomarker_list: protocolPayload?.biomarkers || protocolPayload?.biomarkerList || 'Not specified',
        must_include: protocolPayload?.mustIncludeCountries || 'Not specified',
        exclude: protocolPayload?.excludeCountries || 'None',
        preferred_countries: protocolPayload?.preferredCountries || 'Not specified',
        affiliate_map: protocolPayload?.affiliateMap || 'Not specified',
        kol_notes: protocolPayload?.kolNotes || 'Not specified',
        has_internal_perf_data: 'Not specified',
        assumption_overrides: 'None',
      };

      const questionSummary = 'Not applicable - Country feasibility analysis based on protocol data only.';

      const instructionPrompt = `You are a highly experienced (10+ years) clinical operations feasibility analyst specializing in oncology trials. You possess deep knowledge of global clinical trial landscapes, regulatory environments, and patient access dynamics. Approach this task from a strategic perspective, prioritizing data-driven recommendations that balance speed, cost, and enrollment potential while considering Daiichi Sankyo's specific needs and historical performance. Your recommendations should be actionable and clearly justified.

Today's date: {{today_date}}

This task involves producing a rigorous, data-backed country-level feasibility report to recommend countries for a clinical trial sponsored by Daiichi Sankyo. The recommendations should be based on the study's inclusion/exclusion (I/E) criteria, high-level design, and specific sponsor context. The objective is to quantify addressable eligible patients and monthly randomization potential per country, evaluate key feasibility factors, and return a ranked country portfolio that can meet the target sample size within the specified enrollment duration, with a buffer.

**Task Instructions:**

Analyze the provided inputs to generate a comprehensive country-level feasibility report. The report must include the following sections, in the specified order, with the exact headings:

**Sections to Produce (STRICT order and headings):**

1) Incidence of the Specific Disease Under Consideration

Report age-standardized incidence per 100k (or relevant prevalence if appropriate), total annual incident cases, and calculated eligible pool using I/E filters.

Show Eligible_y(c) and Eligible_m(c) with assumptions for biomarker prevalence and screen failure.

Table A1 (per country): Country | ASR/100k | Annual incident cases | p_subpop | p_biomarker+ | p_tested | Consent% | Screen-fail% | Eligible_y | Eligible_m

2) Prior Therapy Requirements

Map I/E prior-therapy rules to local treatment patterns and reimbursement.

Estimate p_prior_therapy_meets_IE from real-world line-of-therapy distributions.

Score Prior-Therapy Alignment = {High / Moderate / Low} with rationale.

Table B1: Country | Required prior regimens | Availability/reimbursed? | % patients meeting prior-therapy I/E | Alignment (H/M/L) | Notes

3) Biomarker Test

For each required biomarker: positivity prevalence, test coverage (% patients actually tested), lab capacity, turnaround time (TAT), local vs central testing feasibility, and assay concordance expectations.

Compute Biomarker Readiness Index (0–100) combining coverage, capacity, and TAT (define weights).

Table C1: Country | Biomarker(s) | Positivity% | Testing coverage% | Lab capacity | Typical TAT (days) | Central-lab viable? | Readiness Index (0–100) | Gaps & mitigations

4) Financial Feasibility

Provide start-up cost (ethics/authority fees, translations, contracting), per-site fixed costs, per-patient variable costs (procedures, SOC gaps, pharmacy, imaging), pass-throughs (shipping, central lab), and currency considerations.

Estimate CPRP and budget share if selected. Flag high-variance cost drivers.

Table D1: Country | Start-up USD | Fixed/site USD | Variable/patient USD | Expected rpm | CPRP USD | Budget share% | Cost risks & notes

5) Timeline for Activation

Estimate start-up cycle time (contracting, EC/IRB, authority/CTA, import license, site qualification), typical FPI lag, and site ramp.

Show activation risk (Low/Med/High) and critical path items.

Table E1: Country | Contracting days | EC/IRB days | Authority days | Import/license days | Start-up total | Median time to FPI | Activation risk

6) Regulatory Strategy

Identify applicable framework (e.g., EU CTR 536/2014, US IND/IDE/EC, UK MHRA, JP PMDA, CN NMPA, etc.), submission package expectations, safety reporting, data privacy/localization, and biomarker/companion-diagnostic implications.

Provide Regulatory Complexity Score (0–100; higher = harder) with rationale and mitigation (e.g., parallel submissions, scientific advice).

Table F1: Country | Framework | Key requirements | Parallelization options | Complexity (0–100) | Mitigations

7) Competition & Historical Performance (Supportive)

Active recruiting/NYR trials with overlapping indication/line/biomarker; compute Competition Index and narrative on crowding.

Historical country performance: median site rpm, screen-fail rate, startup timelines, data quality flags.

Table G1: Country | Active competing trials | Competition Index | Historical median site rpm | Historical startup (days) | Data quality notes

8) Sponsor (Daiichi Sankyo) Context

Local affiliate presence, CRO partners, prior relationships/KOLs, procurement constraints, import/export experience.

Table H1: Country | DS affiliate? | Known site relationships | CRO coverage | Prior performance notes | Leverage opportunities

9) Country Recommendation & Portfolio Fit

Compute a Composite Country Score (0–100) using default weights (override if input provides weights):

Incidence/eligible pool 25%

Prior therapy alignment 10%

Biomarker readiness 15%

Financial feasibility 15%

Activation timeline 15%

Regulatory strategy 10%

Competition & historical performance 5%

Sponsor context 5%

Produce ranked list and propose must-have / optional / backup country sets with # sites, expected rpm, and contribution to N. Verify portfolio achieves target_n within enrollment_months with ≥10–20% buffer.

Table I1: Rank | Country | Composite Score | Proposed sites | Expected rpm | Projected contribution to N | Role (Must/Opt/Backup) | Key risks

10) Risks & Mitigations

Summarize cross-cutting risks (e.g., low testing coverage, import delays, SDV burden) and specific mitigations (e.g., central lab, patient-finder, remote monitoring, backup sites).

Table J1: Risk | Likelihood | Impact | Mitigation | Owner

11) Assumptions & Sensitivity

List all assumptions (consent%, screen-fail%, positivity%, testing coverage, rpm per site, costs).

Provide at least two sensitivity scenarios (e.g., −20% testing coverage, +25% competition) and show impact on portfolio rpm and whether target still met.

12) Citations

Numbered list with source name and link; include access date. Prefer national registries, peer-reviewed literature, and trial registries within last 36 months; flag older sources.

Incorporate competition, historical performance, standard-of-care alignment, and sponsor (Daiichi Sankyo) context. Analyze and summarize the competitive landscape (number of trials, similar protocols), Daiichi Sankyo's historical enrollment performance in relevant trials within the specified countries, alignment of the study protocol with local standard-of-care guidelines, and any relevant internal Daiichi Sankyo strategic priorities or limitations. Provide specific data points and sources for each.

Calculate expected randomizations per month (rpm) per country, considering average site enrollment rates for similar trials in that country, estimated site activation timelines, and potential operational challenges (e.g., supply chain issues, regulatory hurdles). Explicitly state the assumptions used for site throughput and operational factors.

Return a ranked list of countries categorized as 'must-have,' 'optional,' or 'backup,' based on their projected ability to contribute to achieving the target sample size (target_n) within the specified enrollment duration (enrollment_months). Justify the ranking and categorization for each country, including a contingency buffer (e.g., 20% over-enrollment target) to account for potential underperformance.

**Inputs:**

Indication & Subtype: {{indication}}, {{subtype_or_line_of_therapy}}

Study Design (phase, arms, control, blinding): {{study_design_overview}}

Target Sample Size & Enrollment Duration: target_n={{target_sample_size}}, enrollment_months={{enrollment_months}}

Inclusion Criteria: {{inclusion_criteria}}

Exclusion Criteria: {{exclusion_criteria}}

Biomarker(s) & Required Tests: {{biomarker_list}}

Geographic constraints: must_include={{must_include}}, exclude={{exclude}}

Sponsor context (Daiichi Sankyo): preferred_countries={{preferred_countries}}, affiliate_map={{affiliate_map}}, kol_notes={{kol_notes}}

Internal historical performance available? {{has_internal_perf_data}}

Assumption overrides (optional): {{assumption_overrides}}

Site Capacity: average_patients_per_site={{avg_patients_per_site}}, site_infrastructure_requirements={{site_infrastructure_requirements}}

Investigator Experience: investigator_experience={{investigator_experience}}, investigator_network={{investigator_network}}

Standard of Care: standard_of_care_details={{standard_of_care_details}}, alternative_treatments={{alternative_treatments}}

**Core Calculations (use these formulas and show your working):**

Eligible patients per year (country c)

Eligibley(c)=Incidencey(c)×psubpop×pprior_therapy_meets_IE×pbiomarker+×ptested×pconsent×(1−pscreen_fail)

Eligible per month: Eligiblem(c)=Eligibley(c)/12

Expected randomizations per month (rpm): adjust for site throughput and operational factors.

Competition Index (example):

CI(c) = \\frac{\\text{Active competing trials or planned competing N}}{\\text{Eligible_y(c)}} (define your exact method)

Cost per randomized patient (CPRP) (example):

CPRP(c)=randomized patientsstartup + fixed + variable (per-patient)

**Output Format (STRICT):**

CRITICAL: You MUST include a valid JSON block at the end of your response. The JSON is REQUIRED and must be complete.

Executive Summary (≤10 bullets): Provide a concise executive summary highlighting key findings, recommended country portfolio, projected enrollment feasibility, and critical risks. Include brief methodology overview (formulas used, calculation approach, and logical steps for table generation).

Sectioned Narrative for Sections 1–12 above (use the exact headings): For each section, explain the clinical and operational logic, data sources, assumptions, and how the calculations were applied. Show your working for key formulas (Eligible_y, Eligible_m, Competition Index, CPRP, Composite Score).

Ranked Country Table (I1) and all referenced tables (A1–J1): All tables must be fully populated with calculated values derived from the formulas and methodology described above.

MANDATORY JSON BLOCK: At the end of your response, you MUST include a complete, valid JSON object matching the schema below. Do NOT wrap it in markdown code blocks. The JSON must start with { and end with }. It must contain all required fields with actual data (not placeholders).

**CRITICAL JSON FORMATTING REQUIREMENTS:**

1. **NO HTML TAGS**: All string fields in the JSON must contain plain text ONLY. Do NOT include HTML tags like <p>, <ul>, <li>, <h3>, <strong>, etc. Use plain text with line breaks represented as \\n if needed.

2. **NUMERIC FIELDS**: All numeric fields must be actual numbers (not strings). For example:
   - CORRECT: "target_n": 150
   - WRONG: "target_n": "150 (Assumed)"
   - CORRECT: "asr_per_100k": 1.7
   - WRONG: "asr_per_100k": "1.7"

3. **TEXT FIELDS**: Remove all HTML markup from text fields. Convert HTML to plain text:
   - Convert <p>text</p> to just "text"
   - Convert <ul><li>item</li></ul> to "item" or "• item"
   - Convert <strong>text</strong> to just "text"
   - Convert <h3>text</h3> to just "text"

4. **ACTIVATION_RISK**: Must be one of: "Low", "Medium", "High" (plain text, no HTML)

5. **ALIGNMENT**: Must be one of: "High", "Moderate", "Low" (plain text, no HTML)

Machine-Readable JSON block with this structure:

{
  "inputs_summary": { },
  "weights_used": {
    "incidence": 0.25,
    "prior_therapy": 0.10,
    "biomarker": 0.15,
    "financial": 0.15,
    "timeline": 0.15,
    "regulatory": 0.10,
    "competition_historical": 0.05,
    "sponsor_context": 0.05
  },
  "countries": [
    {
      "country": "string",
      "metrics": {
        "incidence": { "asr_per_100k": 0.0, "annual_incident": 0, "eligible_y": 0, "eligible_m": 0.0, "assumptions": [] },
        "prior_therapy": { "pct_meeting_IE": 0.0, "alignment": "High|Moderate|Low", "notes": "" },
        "biomarker": { "positivity_pct": 0.0, "testing_coverage_pct": 0.0, "capacity_note": "", "tat_days": 0, "readiness_index": 0 },
        "financial": { "startup_usd": 0, "fixed_per_site_usd": 0, "variable_per_patient_usd": 0, "expected_rpm": 0.0, "cprp_usd": 0, "budget_share_pct": 0, "cost_risks": "" },
        "timeline": { "contracting_days": 0, "ec_irb_days": 0, "authority_days": 0, "import_days": 0, "startup_total_days": 0, "time_to_fpi_days": 0, "activation_risk": "Low|Medium|High" },
        "regulatory": { "framework": "", "key_requirements": "", "parallelization": "", "complexity_score": 0, "mitigations": "" },
        "competition_historical": { "active_competing_trials": 0, "competition_index": 0.0, "historical_site_rpm": 0.0, "historical_startup_days": 0, "data_quality_notes": "" },
        "sponsor_context": { "affiliate": "Y|N|note", "site_relationships": "", "cro_coverage": "", "leverage_opportunities": "" }
      },
      "composite_score": 0.0,
      "proposed_sites": 0,
      "projected_contribution_to_N": 0,
      "role": "Must|Optional|Backup",
      "key_risks": "",
      "citations": [1,2]
    }
  ],
  "recommended_portfolio": {
    "must_have": ["CountryA","CountryB"],
    "optional": ["CountryC"],
    "backup": ["CountryD"]
  },
  "feasibility_check": {
    "target_n": {{target_sample_size}},
    "enrollment_months": {{enrollment_months}},
    "projected_total_randomizations": 0,
    "meets_target": true
  },
  "assumptions": ["..."],
  "sensitivity": [
    { "scenario": "string", "projected_total_randomizations": 0, "meets_target": true }
  ]
}

**Constraints/Guardrails:**

1.  **Minimum Enrollment Threshold:** If projected monthly randomization potential (rpm) for a country falls below 3 patients, flag as "High Risk" regardless of ranking.
2.  **Regulatory Approval Timeline:** Prioritize countries where regulatory approval timelines are demonstrably shorter than half of \`enrollment_months\`. Justify exceptions.
3.  **Data Source Reliability:** Explicitly state the source and confidence level for each data point used in \`Eligibley(c)\` calculation. Flag data points with low confidence.
4.  **Sponsor Preference Override:** If \`preferred_countries\` cannot meet 50% of \`target_n\` within \`enrollment_months\`, clearly state the shortfall and propose alternative countries.
5.  **Budgetary Limits:** If financial feasibility analysis reveals costs exceeding a pre-defined threshold (e.g., 20% above average cost per patient across all countries), flag the country and provide mitigation strategies.

Use absolute dates and cite sources (no hidden chain-of-thought).

Be explicit about filters/ICD codes/registry queries used.

If data are missing, state the gap and use a conservative assumption, clearly labeled.

Keep narrative concise; put detail in tables & JSON.

Optional: Example Input JSON (replace as needed)
{
  "indication": "High-grade serous ovarian cancer",
  "subtype_or_line_of_therapy": "Platinum-resistant, 2L+",
  "study_design_overview": "Phase 3, randomized, R-DXd vs physician’s choice",
  "target_sample_size": 360,
  "enrollment_months": 16,
  "inclusion_criteria": ["Adults ≥18y", "Platinum-resistant disease", "Prior ≥1 line platinum", "Meets organ function thresholds"],
  "exclusion_criteria": ["Untreated symptomatic CNS mets", "Severe ILD history"],
  "biomarker_list": ["HER2 IHC", "NGS panel (optional)"],
  "must_include": ["US","JP"],
  "exclude": [],
  "preferred_countries": ["US","JP","DE","UK","ES","IT","KR"],
  "affiliate_map": {"US": "Yes", "JP": "Yes", "EU": "Yes"},
  "kol_notes": "Strong KOL ties in US/JP/DE.",
  "has_internal_perf_data": "no",
  "assumption_overrides": ["Consent 65%", "Screen failure 25%", "Testing coverage 70% in major centers"],
  "avg_patients_per_site": 5,
  "site_infrastructure_requirements": "Standard oncology clinic infrastructure",
  "investigator_experience": "Extensive experience in ovarian cancer trials",
  "investigator_network": "Strong network of investigators in major cancer centers",
  "standard_of_care_details": "Platinum-based chemotherapy followed by PARP inhibitors",
  "alternative_treatments": "Bevacizumab, other chemotherapies"
}`;

      let prompt = instructionPrompt;
      Object.entries(placeholders).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        prompt = prompt.replace(regex, value);
      });

      prompt += `\n\nNote: This analysis is based on protocol data. Questionnaire responses are not required for country feasibility generation.\n`;
      prompt += '\nProvide the report followed by the JSON block as specified.';
      prompt += '\n\nCRITICAL JSON FORMATTING REQUIREMENTS:';
      prompt += '\n1. Ensure ALL tables (A1 through J1) are fully completed, especially Table J1 (Risks & Mitigations). Do not truncate or cut off any table content.';
      prompt += '\n2. ALL string fields in JSON must be plain text with NO HTML tags. Remove all <p>, <ul>, <li>, <h3>, <strong>, and other HTML markup.';
      prompt += '\n3. ALL numeric fields must be actual numbers (not strings). Use 150 not "150 (Assumed)".';
      prompt += '\n4. Convert any HTML content to plain text before including in JSON fields.';

      // Build protocol context with complete protocol
      let protocolContext = '';
      if (protocolPayload) {
        const protocolSummary = {
          projectId: protocolPayload?.projectId || effectiveProjectId,
          protocolNumber: protocolNumber || protocolPayload?.protocolNumber || protocolPayload?.number || 'Not specified',
        };

        // Include all protocol sections (complete protocol)
        const sections = protocolPayload?.sections || [];
        
        if (Array.isArray(sections) && sections.length > 0) {
          protocolSummary.sections = sections.map((section) => {
            const content = section.output || section.context || '';
            return {
              title: section.title,
              number: section.number || section.order || null,
              content: content,
            };
          });
        }

        protocolContext = `INCLUSION CRITERIA:\n${inclusionText || 'Not provided.'}\n\nEXCLUSION CRITERIA:\n${exclusionText || 'Not provided.'}\n\nCOMPLETE PROTOCOL:\n${JSON.stringify(protocolSummary, null, 2)}`;
      } else {
        protocolContext = `INCLUSION CRITERIA:\n${inclusionText || 'Not provided.'}\n\nEXCLUSION CRITERIA:\n${exclusionText || 'Not provided.'}`;
      }

      const rawResponse = await generateContent(
        prompt,
        protocolContext,
        '',
        'feasibility',
        'Country Feasibility Report'
      );

      const { cleanedText, json } = extractJsonFromResponse(rawResponse);
      let responseText = cleanedText;
      let structuredJson = json;

      const cleanedNarrative = /<\/?[a-z][\s\S]*>/i.test(responseText)
        ? htmlToPlainText(responseText)
        : responseText.trim();

      let extractedTablesFromHtml = {};
      if (!structuredJson) {
        extractedTablesFromHtml = parseHtmlTables(rawResponse);
      }

      // If JSON is missing, try to extract it more aggressively or generate it
      if (!structuredJson) {
        // Try to find JSON in the raw response (before cleaning)
        const rawJsonParse = extractJsonFromResponse(rawResponse);
        if (rawJsonParse.json) {
          structuredJson = rawJsonParse.json;
        } else {
          // Try fallback JSON generation from narrative
          try {
            console.log('Attempting fallback JSON generation from narrative...');
          const fallbackJsonResponse = await requestStructuredJson({
            narrative: cleanedNarrative,
            placeholders,
            questionSummary: 'Not applicable - Analysis based on protocol data only.',
          });
          const fallbackParse = extractJsonFromResponse(fallbackJsonResponse || '');
          if (fallbackParse.json) {
            structuredJson = fallbackParse.json;
              console.log('Fallback JSON generation succeeded');
            } else {
              console.warn('Fallback JSON generation returned no valid JSON');
          }
        } catch (fallbackErr) {
          console.warn('Fallback JSON generation failed:', fallbackErr);
          }
        }
      }

      // Set the AI response text (contains Executive Summary and narrative)
      setReportText(cleanedNarrative);
      setReportJson(structuredJson || null);
      
      // Set technical processing notes (separate from Executive Summary)
      // These notes are for debugging/transparency, not the actual Executive Summary
      if (structuredJson) {
        setFallbackTables({});
        setUsedFallbackTables(false);
        setSummaryNote(
          [
            'Tables generated via Gemini structured JSON output.',
            'Key inputs:',
            '1. Feasibility questionnaire Q&A responses (cleaned of HTML).',
            '2. Protocol-derived inclusion/exclusion text pulled from Document Editor sections when available.',
            '3. Static prompt instructions defining required sections, calculations, and JSON schema.',
            'Processing steps:',
            '- Gemini returns narrative plus JSON; JSON is parsed client-side.',
            '- Parsed metrics feed the snapshot cards and bordered tables (A1–J1, I1).',
            '- Machine-readable block is rendered in the JSON viewer at the bottom.',
            'Validation:',
            '- No fallback parsing used; values come directly from Gemini JSON. Review still recommended before export.'
          ].join('\n')
        );
      } else {
        const narrativeTables = extractedTablesFromHtml && Object.keys(extractedTablesFromHtml).length
          ? extractedTablesFromHtml
          : extractFallbackTables(cleanedNarrative);
        setFallbackTables(narrativeTables);
        const hasTables = Object.keys(narrativeTables).length > 0;
        setUsedFallbackTables(hasTables);
        setSummaryNote(
          [
            'Structured JSON missing; tables reconstructed from Gemini narrative.',
            'Key inputs:',
            '1. Same prompt and protocol criteria as structured flow.',
            '2. Gemini only returned HTML/markdown narrative.',
            'Processing steps:',
            '- Attempted to parse <table> elements from narrative; if absent, extracted tabular text heuristically.',
            '- Reconstructed bordered tables from parsed headers/rows; values may need manual cleanup.',
            hasTables
              ? '- Snapshot cards use available narrative metrics; missing values show as em dashes.'
              : '- No tables detected; only narrative text is available for review.',
            'Validation:',
            '- Please verify figures before export; consider regenerating if critical fields are blank.'
          ].join('\n')
        );
      }

    } catch (generateError) {
      console.error('Country feasibility generation failed:', generateError);
      setError(generateError?.message || 'Failed to generate country feasibility analysis.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!effectiveProjectId) {
      setSaveAlert({ severity: 'error', message: 'Please select a project first.' });
      return;
    }

    const hasAnyReportContent =
      (reportJson && Object.keys(reportJson).length > 0) ||
      !!reportText ||
      (fallbackTables && Object.keys(fallbackTables).length > 0);

    if (!hasAnyReportContent) {
      setSaveAlert({ severity: 'error', message: 'Generate a report before saving.' });
      return;
    }

    setSaving(true);
    setSaveAlert(null);

    try {
      await feasibilityService.saveCountryReport(effectiveProjectId, {
        reportText,
        reportJson,
        summaryNote,
        fallbackTables,
        usedFallbackTables,
        citations: citationsData,
      });
      setSaveAlert({ severity: 'success', message: 'Country feasibility report saved.' });
    } catch (saveErr) {
      console.error('Failed to save country feasibility report:', saveErr);
      setSaveAlert({
        severity: 'error',
        message: saveErr?.response?.data?.message || saveErr?.message || 'Failed to save report.',
      });
    } finally {
      setSaving(false);
    }
  };

  const extractExecutiveSummary = (text = '') => {
    if (!text) return null;
    // Look for "Executive Summary" section
    const execSummaryMatch = text.match(/(?:Executive Summary|EXECUTIVE SUMMARY)[\s\S]*?(?=\d+\)\s|Table\s+[A-Z]\d|Sectioned Narrative|$)/i);
    if (execSummaryMatch) {
      let summary = execSummaryMatch[0]
        .replace(/^(?:Executive Summary|EXECUTIVE SUMMARY)[:\s]*/i, '')
        .trim();
      // Remove any table references that might have been included
      summary = summary.split(/\n\s*Table\s+[A-Z]\d/i)[0].trim();
      return summary;
    }
    return null;
  };

  const renderExecutiveSummary = () => {
    // Extract Executive Summary from the AI response (reportText)
    const execSummary = reportText ? extractExecutiveSummary(reportText) : null;
    // Only show if we have either the extracted summary or report data
    if (!execSummary && !reportJson) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#fafafa' }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
          Executive Summary
        </Typography>
        {execSummary ? (
          <Box sx={{ mb: 3 }}>
            {execSummary.split(/\n+/).filter(Boolean).map((bullet, idx) => (
              <Typography key={idx} variant="body2" sx={{ mb: 1.5, pl: 2, position: 'relative' }}>
                <Box component="span" sx={{ position: 'absolute', left: 0, color: 'primary.main' }}>
                  •
                </Box>
                {bullet.replace(/^[-•*]\s*/, '').trim()}
              </Typography>
            ))}
          </Box>
        ) : null}
        
        <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
          Methodology & Calculation Approach
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Core Calculations Applied:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body2" component="span">
                <strong>Eligible Patients per Year:</strong> Eligible_y(c) = Incidence_y(c) × p_subpop × p_prior_therapy_meets_IE × p_biomarker+ × p_tested × p_consent × (1 - p_screen_fail)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Eligible Patients per Month:</strong> Eligible_m(c) = Eligible_y(c) / 12
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Competition Index:</strong> CI(c) = Active competing trials / Eligible_y(c)
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Cost per Randomized Patient:</strong> CPRP(c) = (Startup + Fixed + Variable costs) / Randomized patients
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Composite Country Score:</strong> Weighted average of 8 factors (Incidence 25%, Prior therapy 10%, Biomarker 15%, Financial 15%, Timeline 15%, Regulatory 10%, Competition 5%, Sponsor context 5%)
              </Typography>
            </li>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Table Generation Logic:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table A1 (Incidence):</strong> Calculates eligible patient pool by applying I/E criteria filters to age-standardized incidence rates, accounting for biomarker prevalence, testing coverage, consent rates, and screen failure rates.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table B1 (Prior Therapy):</strong> Maps inclusion/exclusion prior-therapy requirements to local treatment patterns and reimbursement availability, estimating the percentage of patients meeting I/E criteria.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table C1 (Biomarker):</strong> Evaluates biomarker testing readiness by combining positivity prevalence, testing coverage percentage, lab capacity, and turnaround time into a Readiness Index (0-100).
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table D1 (Financial):</strong> Aggregates start-up costs, per-site fixed costs, and per-patient variable costs to calculate CPRP and budget share projections.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table E1 (Timeline):</strong> Sums contracting, EC/IRB, regulatory authority, and import license timelines to estimate total start-up duration and time to first patient in (FPI).
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table F1 (Regulatory):</strong> Assesses regulatory complexity (0-100) based on framework requirements, parallelization options, and mitigation strategies.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table G1 (Competition):</strong> Computes Competition Index by dividing active competing trials by eligible patient pool, incorporating historical site performance metrics.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table H1 (Sponsor Context):</strong> Evaluates Daiichi Sankyo's local presence, CRO coverage, and leverage opportunities based on affiliate status and prior relationships.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table I1 (Portfolio):</strong> Ranks countries using Composite Score, calculates projected contribution to target N, and categorizes as Must-have/Optional/Backup based on ability to meet enrollment targets with buffer.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Table J1 (Risks):</strong> Identifies cross-cutting risks (testing coverage, import delays, SDV burden) and specific mitigations with assigned ownership.
            </Typography>
          </li>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'text.primary' }}>
          Clinical Feasibility Assessment:
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
          Each country's feasibility is evaluated through a systematic assessment of patient availability (incidence and eligibility), 
          treatment alignment (prior therapy requirements), diagnostic readiness (biomarker testing), operational feasibility (financial 
          and timeline), regulatory complexity, competitive landscape, and sponsor infrastructure. The Composite Score provides a 
          quantitative ranking that balances enrollment potential, cost efficiency, and operational risk. Portfolio recommendations 
          ensure the target sample size can be achieved within the specified enrollment window with a 10-20% contingency buffer.
        </Typography>
      </Paper>
    );
  };

  const renderSummary = () => {
    if (!reportJson?.countries || !reportJson.countries.length) return null;
    const sortedCountries = reportJson.countries
      .slice()
      .sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0));
    const eligibleTotal = sortedCountries.reduce(
      (sum, country) => sum + (country.metrics?.incidence?.eligible_y || 0),
      0
    );

    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            minWidth: 220,
            flex: '0 0 auto',
            background: 'linear-gradient(135deg, #f0f6ff 0%, #ffffff 100%)',
          }}
        >
          <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
            Feasibility Snapshot
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Auto-generated summary using questionnaire context and protocol criteria.
          </Typography>
          <Box sx={{ display: 'grid', rowGap: 1.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {sortedCountries.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Countries in scope
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {eligibleTotal.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Eligible caseload / yr
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          {sortedCountries.map((country, index) => (
            <Paper
              key={country.country || index}
              variant="outlined"
              sx={{ p: 2.5, mb: 1.5, borderRadius: 2, borderLeft: '4px solid #1d6fec' }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {index + 1}. {country.country}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (priority {(country.composite_score || 0).toFixed(3)})
                </Typography>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Incidence (std): {country.metrics?.incidence?.asr_per_100k || '—'}/100k · Eligible pool:{' '}
                {(country.metrics?.incidence?.eligible_y || 0).toLocaleString()} / yr · Biomarker coverage:{' '}
                {country.metrics?.biomarker?.testing_coverage_pct != null
                  ? `${country.metrics.biomarker.testing_coverage_pct}%`
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prior-therapy fit:{' '}
                {country.metrics?.prior_therapy?.pct_meeting_IE != null
                  ? `${country.metrics.prior_therapy.pct_meeting_IE}% (${country.metrics?.prior_therapy?.alignment || '—'})`
                  : '—'}
                · RPM: {
                  country.metrics?.financial?.expected_rpm != null
                    ? country.metrics.financial.expected_rpm.toFixed(2)
                    : '—'
                }
                · Start-up: $
                {country.metrics?.financial?.startup_usd != null
                  ? country.metrics.financial.startup_usd.toLocaleString()
                  : '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Regulatory: {country.metrics?.timeline?.time_to_fpi_days ? `${Math.round(country.metrics.timeline.time_to_fpi_days / 30)} mo` : '—'} ·
                Competition index:{' '}
                {country.metrics?.competition_historical?.competition_index != null
                  ? country.metrics.competition_historical.competition_index.toFixed(2)
                  : '—'}
                · Sponsor presence: {country.metrics?.sponsor_context?.affiliate || '—'}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  };

  const renderCountryRanking = () => {
    if (!reportJson?.countries || !reportJson.countries.length) return null;

    const sortedCountries = reportJson.countries
      .slice()
      .sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0));

    const toPercentage = (value) =>
      value != null ? `${Number(value).toFixed(0)}%` : '—';

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Country Ranking
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid rgba(224,224,224,1)' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <TableCell>#</TableCell>
                <TableCell>Country</TableCell>
                <TableCell align="right">STD rate /100k</TableCell>
                <TableCell align="right">Eligible / yr</TableCell>
                <TableCell align="right">SoC fit</TableCell>
                <TableCell align="right">Bio testing</TableCell>
                <TableCell align="right">Historical rpm</TableCell>
                <TableCell align="right">Competing trials</TableCell>
                <TableCell align="right">Reg (mo)</TableCell>
                <TableCell align="right">Start-up ($K)</TableCell>
                <TableCell align="center">DS presence</TableCell>
                <TableCell align="right">Priority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCountries.map((country, index) => {
                const incidence = country.metrics?.incidence || {};
                const prior = country.metrics?.prior_therapy || {};
                const biomarker = country.metrics?.biomarker || {};
                const financial = country.metrics?.financial || {};
                const timeline = country.metrics?.timeline || {};
                const competition = country.metrics?.competition_historical || {};
                const sponsor = country.metrics?.sponsor_context || {};
                return (
                  <TableRow key={country.country || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{country.country}</TableCell>
                    <TableCell align="right">{incidence.asr_per_100k != null ? incidence.asr_per_100k.toFixed(1) : '—'}</TableCell>
                    <TableCell align="right">
                      {incidence.eligible_y != null ? incidence.eligible_y.toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">{toPercentage(prior.pct_meeting_IE)}</TableCell>
                    <TableCell align="right">{toPercentage(biomarker.testing_coverage_pct)}</TableCell>
                    <TableCell align="right">
                      {competition.historical_site_rpm != null
                        ? competition.historical_site_rpm.toFixed(2)
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {competition.active_competing_trials != null ? competition.active_competing_trials : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {timeline.time_to_fpi_days != null ? (timeline.time_to_fpi_days / 30).toFixed(1) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {financial.startup_usd != null ? (financial.startup_usd / 1000).toFixed(0) : '—'}
                    </TableCell>
                    <TableCell align="center">{sponsor.affiliate || '—'}</TableCell>
                    <TableCell align="right">{(country.composite_score || 0).toFixed(3)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderNarrativeBlock = (block, index) => {
    const trimmed = block.trim();
    if (!trimmed) return null;

    const headingPattern = /^(Executive Summary|\d+\.?\d*\)|\d+\.?\d*\s|Section\s|Appendix\s|Objectives$|To:|From:|Date:|Subject:)/i;
    const bulletLineRegex = /^([•*\-])\s*/;
    const containsBullet = /(^|\n)[•*\-]\s+/m.test(trimmed);

    const lines = trimmed.split('\n').map((line) => line.trim()).filter(Boolean);
    const firstLine = lines[0];
    const remainder = lines.slice(1).join('\n');

    const buildBulletList = (text, keySuffix) => {
      const items = text
        .split('\n')
        .map((item) => item.trim())
        .filter((item) => bulletLineRegex.test(item));
      if (!items.length) return null;
      return (
        <Box component="ul" key={`${index}-${keySuffix}`} sx={{ pl: 3, mb: 1.5 }}>
          {items.map((item, itemIdx) => (
            <Typography component="li" variant="body2" key={`${index}-${keySuffix}-${itemIdx}`} sx={{ lineHeight: 1.7 }}>
              {item.replace(bulletLineRegex, '')}
            </Typography>
          ))}
        </Box>
      );
    };

    const renderParagraph = (text, keySuffix) => (
      <Typography key={`${index}-${keySuffix}`} variant="body2" sx={{ lineHeight: 1.7 }}>
        {text.trim()}
      </Typography>
    );

    if (firstLine && headingPattern.test(firstLine)) {
      const elements = [
        <Typography key={`${index}-heading`} variant="subtitle1" sx={{ fontWeight: 600, mt: headingPattern.test(firstLine) ? 2 : 0 }}>
          {firstLine}
        </Typography>,
      ];

      const remainderContent = remainder.trim();
      if (remainderContent) {
        if (/(^|\n)[•*\-]\s+/m.test(remainderContent)) {
          const list = buildBulletList(remainderContent, 'heading-list');
          if (list) elements.push(list);
        } else {
          const paragraphs = remainderContent.split(/\n{2,}/).filter(Boolean);
          paragraphs.forEach((para, idx) => {
            elements.push(renderParagraph(para, `heading-para-${idx}`));
          });
        }
      }

      return <React.Fragment key={`block-${index}`}>{elements}</React.Fragment>;
    }

    if (containsBullet) {
      const list = buildBulletList(trimmed, 'list');
      return list ? <React.Fragment key={`block-${index}`}>{list}</React.Fragment> : null;
    }

    return renderParagraph(trimmed, 'paragraph');
  };

  const renderReportText = () => {
    if (!summaryNote) return null;
    const blocks = summaryNote.split(/\n+/).filter(Boolean);
    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Generation Summary
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {blocks.map((block, index) => renderNarrativeBlock(block, index))}
        </Box>
      </Paper>
    );
  };

  const formatNumber = (value, digits = 0) => {
    if (value == null) return '—';
    const numeric = Number(
      typeof value === 'string' ? value.replace(/[^0-9.+-]/g, '') : value
    );
    if (Number.isNaN(numeric)) return '—';
    return digits > 0 ? numeric.toFixed(digits) : numeric.toLocaleString();
  };

  const formatPercent = (value, digits = 0) => {
    if (value == null) return '—';
    const numeric = Number(
      typeof value === 'string' ? value.replace(/[^0-9.+-]/g, '') : value
    );
    if (Number.isNaN(numeric)) return '—';
    return `${numeric.toFixed(digits)}%`;
  };

  const formatCurrency = (value) => {
    if (value == null) return '—';
    const numeric = Number(
      typeof value === 'string' ? value.replace(/[^0-9.+-]/g, '') : value
    );
    if (Number.isNaN(numeric)) return '—';
    return `$${numeric.toLocaleString()}`;
  };

  const formatBoolean = (value) => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' && value.length) return value;
    return '—';
  };

  const renderMetricTable = (tableId, title, columns, buildRow) => {
    // Check if we have countries data
    if (!countriesForTables || !countriesForTables.length) return null;
    
    // Build rows from countries data
    const rows = countriesForTables
      .map((country, idx) => buildRow(country, idx))
      .filter(Boolean);

    // Only return null if no valid rows were generated
    // This allows tables to show even if some countries have missing data
    if (!rows.length) return null;

    return (
      <Paper key={tableId} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid rgba(224,224,224,1)' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                {columns.map((column) => (
                  <TableCell key={`${tableId}-${column.key}`} align={column.align || 'left'}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  {row.cells.map((cell, cellIdx) => (
                    <TableCell key={`${row.key}-${cellIdx}`} align={columns[cellIdx]?.align || 'left'}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderTableA1 = () =>
    renderMetricTable(
      'A1',
      '1) Incidence of the Specific Disease Under Consideration',
      [
        { key: 'country', label: 'Country' },
        { key: 'asr', label: 'ASR /100k', align: 'right' },
        { key: 'incident', label: 'Annual incident cases', align: 'right' },
        { key: 'subpop', label: 'p_subpop', align: 'right' },
        { key: 'biomarker', label: 'p_biomarker+', align: 'right' },
        { key: 'tested', label: 'p_tested', align: 'right' },
        { key: 'consent', label: 'Consent%', align: 'right' },
        { key: 'screen', label: 'Screen-fail%', align: 'right' },
        { key: 'eligibleY', label: 'Eligible_y', align: 'right' },
        { key: 'eligibleM', label: 'Eligible_m', align: 'right' },
      ],
      (country) => {
        const incidence = country.metrics?.incidence || {};
        const biomarker = country.metrics?.biomarker || {};
        return {
          key: `A1-${country.country}`,
          cells: [
            country.country,
            formatNumber(incidence.asr_per_100k, 1),
            formatNumber(incidence.annual_incident),
            formatPercent(incidence.p_subpop ?? incidence.subpop_pct ?? incidence.subpopulation_pct),
            formatPercent(biomarker.positivity_pct),
            formatPercent(biomarker.testing_coverage_pct),
            formatPercent(incidence.consent_pct),
            formatPercent(incidence.screen_fail_pct),
            formatNumber(incidence.eligible_y),
            formatNumber(incidence.eligible_m, 1),
          ],
        };
      }
    );

  const renderTableB1 = () =>
    renderMetricTable(
      'B1',
      '2) Prior Therapy Requirements',
      [
        { key: 'country', label: 'Country' },
        { key: 'required', label: 'Required prior regimens' },
        { key: 'availability', label: 'Availability/reimbursed?' },
        { key: 'pctMeeting', label: '% meeting I/E', align: 'right' },
        { key: 'alignment', label: 'Alignment', align: 'center' },
        { key: 'notes', label: 'Notes' },
      ],
      (country) => {
        const prior = country.metrics?.prior_therapy || {};
        return {
          key: `B1-${country.country}`,
          cells: [
            country.country,
            prior.required_regimens || '—',
            prior.availability || '—',
            formatPercent(prior.pct_meeting_IE),
            prior.alignment || '—',
            prior.notes || '—',
          ],
        };
      }
    );

  const renderTableC1 = () =>
    renderMetricTable(
      'C1',
      '3) Biomarker Test',
      [
        { key: 'country', label: 'Country' },
        { key: 'biomarker', label: 'Biomarker(s)' },
        { key: 'positivity', label: 'Positivity%', align: 'right' },
        { key: 'coverage', label: 'Testing coverage%', align: 'right' },
        { key: 'capacity', label: 'Lab capacity' },
        { key: 'tat', label: 'Typical TAT (days)', align: 'right' },
        { key: 'central', label: 'Central-lab viable?' },
        { key: 'readiness', label: 'Readiness Index', align: 'right' },
        { key: 'gaps', label: 'Gaps & mitigations' },
      ],
      (country) => {
        const biomarker = country.metrics?.biomarker || {};
        return {
          key: `C1-${country.country}`,
          cells: [
            country.country,
            biomarker.biomarker || biomarker.name || '—',
            formatPercent(biomarker.positivity_pct),
            formatPercent(biomarker.testing_coverage_pct),
            biomarker.capacity_note || biomarker.lab_capacity || '—',
            formatNumber(biomarker.tat_days),
            formatBoolean(biomarker.central_lab_viable),
            formatNumber(biomarker.readiness_index),
            biomarker.gaps || biomarker.notes || '—',
          ],
        };
      }
    );

  const renderTableD1 = () =>
    renderMetricTable(
      'D1',
      '4) Financial Feasibility',
      [
        { key: 'country', label: 'Country' },
        { key: 'startup', label: 'Start-up USD', align: 'right' },
        { key: 'fixed', label: 'Fixed/site USD', align: 'right' },
        { key: 'variable', label: 'Variable/patient USD', align: 'right' },
        { key: 'rpm', label: 'Expected rpm', align: 'right' },
        { key: 'cprp', label: 'CPRP USD', align: 'right' },
        { key: 'budget', label: 'Budget share%', align: 'right' },
        { key: 'risks', label: 'Cost risks & notes' },
      ],
      (country) => {
        const financial = country.metrics?.financial || {};
        return {
          key: `D1-${country.country}`,
          cells: [
            country.country,
            formatCurrency(financial.startup_usd),
            formatCurrency(financial.fixed_per_site_usd),
            formatCurrency(financial.variable_per_patient_usd),
            financial.expected_rpm != null && !Number.isNaN(Number(financial.expected_rpm))
              ? Number(financial.expected_rpm).toFixed(2)
              : '—',
            formatCurrency(financial.cprp_usd),
            formatPercent(financial.budget_share_pct),
            financial.cost_risks || financial.notes || '—',
          ],
        };
      }
    );

  const renderTableE1 = () =>
    renderMetricTable(
      'E1',
      '5) Timeline for Activation',
      [
        { key: 'country', label: 'Country' },
        { key: 'contracting', label: 'Contracting days', align: 'right' },
        { key: 'ec', label: 'EC/IRB days', align: 'right' },
        { key: 'authority', label: 'Authority days', align: 'right' },
        { key: 'import', label: 'Import/license days', align: 'right' },
        { key: 'startup', label: 'Start-up total (days)', align: 'right' },
        { key: 'fpi', label: 'Median time to FPI', align: 'right' },
        { key: 'risk', label: 'Activation risk', align: 'center' },
      ],
      (country) => {
        const timeline = country.metrics?.timeline || {};
        return {
          key: `E1-${country.country}`,
          cells: [
            country.country,
            formatNumber(timeline.contracting_days),
            formatNumber(timeline.ec_irb_days),
            formatNumber(timeline.authority_days),
            formatNumber(timeline.import_days),
            formatNumber(timeline.startup_total_days),
            timeline.time_to_fpi_days != null && !Number.isNaN(Number(timeline.time_to_fpi_days))
              ? `${Math.round(Number(timeline.time_to_fpi_days) / 30)} mo`
              : '—',
            timeline.activation_risk || '—',
          ],
        };
      }
    );

  const renderTableF1 = () =>
    renderMetricTable(
      'F1',
      '6) Regulatory Strategy',
      [
        { key: 'country', label: 'Country' },
        { key: 'framework', label: 'Framework' },
        { key: 'requirements', label: 'Key requirements' },
        { key: 'parallel', label: 'Parallelization options' },
        { key: 'complexity', label: 'Complexity (0-100)', align: 'right' },
        { key: 'mitigations', label: 'Mitigations' },
      ],
      (country) => {
        const regulatory = country.metrics?.regulatory || {};
        return {
          key: `F1-${country.country}`,
          cells: [
            country.country,
            regulatory.framework || '—',
            regulatory.key_requirements || regulatory.requirements || '—',
            regulatory.parallelization || regulatory.parallelization_options || '—',
            formatNumber(regulatory.complexity_score),
            regulatory.mitigations || '—',
          ],
        };
      }
    );

  const renderTableG1 = () =>
    renderMetricTable(
      'G1',
      '7) Competition & Historical Performance (Supportive)',
      [
        { key: 'country', label: 'Country' },
        { key: 'active', label: 'Active competing trials', align: 'right' },
        { key: 'index', label: 'Competition Index', align: 'right' },
        { key: 'rpm', label: 'Historical median site rpm', align: 'right' },
        { key: 'startup', label: 'Historical startup (days)', align: 'right' },
        { key: 'notes', label: 'Data quality notes' },
      ],
      (country) => {
        const competition = country.metrics?.competition_historical || {};
        return {
          key: `G1-${country.country}`,
          cells: [
            country.country,
            competition.active_competing_trials != null ? competition.active_competing_trials : '—',
            competition.competition_index != null && !Number.isNaN(Number(competition.competition_index))
              ? Number(competition.competition_index).toFixed(2)
              : '—',
            competition.historical_site_rpm != null && !Number.isNaN(Number(competition.historical_site_rpm))
              ? Number(competition.historical_site_rpm).toFixed(2)
              : '—',
            formatNumber(competition.historical_startup_days),
            competition.data_quality_notes || competition.notes || '—',
          ],
        };
      }
    );

  const renderTableH1 = () =>
    renderMetricTable(
      'H1',
      '8) Sponsor (Daiichi Sankyo) Context',
      [
        { key: 'country', label: 'Country' },
        { key: 'affiliate', label: 'DS affiliate?' },
        { key: 'relationships', label: 'Known site relationships' },
        { key: 'cro', label: 'CRO coverage' },
        { key: 'notes', label: 'Leverage opportunities' },
      ],
      (country) => {
        const sponsor = country.metrics?.sponsor_context || {};
        return {
          key: `H1-${country.country}`,
          cells: [
            country.country,
            sponsor.affiliate || '—',
            sponsor.site_relationships || sponsor.kol_notes || '—',
            sponsor.cro_coverage || '—',
            sponsor.leverage_opportunities || sponsor.notes || '—',
          ],
        };
      }
    );

  const renderPortfolioSummary = () => {
    if (!reportJson) return null;
    const portfolio = reportJson.recommended_portfolio || {};
    const feasibility = reportJson.feasibility_check || {};
    const hasPortfolio =
      (portfolio.must_have && portfolio.must_have.length) ||
      (portfolio.optional && portfolio.optional.length) ||
      (portfolio.backup && portfolio.backup.length);
    const hasFeasibility =
      feasibility.target_n != null ||
      feasibility.enrollment_months != null ||
      feasibility.projected_total_randomizations != null;

    if (!hasPortfolio && !hasFeasibility) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Portfolio Recommendation
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {hasPortfolio && (
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Country Sets
              </Typography>
              <Box sx={{ display: 'grid', rowGap: 1 }}>
                {['must_have', 'optional', 'backup'].map((bucketKey) => {
                  const title =
                    bucketKey === 'must_have'
                      ? 'Must-have'
                      : bucketKey === 'optional'
                      ? 'Optional'
                      : 'Backup';
                  const list = portfolio[bucketKey];
                  if (!Array.isArray(list) || !list.length) return null;
                  return (
                    <Box key={bucketKey}>
                      <Typography variant="caption" color="text.secondary">
                        {title}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {list.join(', ')}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
          {hasFeasibility && (
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Enrollment Outlook
              </Typography>
              <Box sx={{ display: 'grid', rowGap: 0.5 }}>
                {feasibility.target_n != null && (
                  <Typography variant="body2">
                    Target N:{' '}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      {feasibility.target_n}
                    </Typography>
                  </Typography>
                )}
                {feasibility.enrollment_months != null && (
                  <Typography variant="body2">
                    Enrollment window:{' '}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      {feasibility.enrollment_months} months
                    </Typography>
                  </Typography>
                )}
                {feasibility.projected_total_randomizations != null && (
                  <Typography variant="body2">
                    Projected randomizations:{' '}
                    <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                      {feasibility.projected_total_randomizations}
                    </Typography>
                  </Typography>
                )}
                {feasibility.meets_target != null && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: feasibility.meets_target ? 'success.main' : 'error.main' }}>
                    Meets target? {feasibility.meets_target ? 'Yes' : 'No'}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  const renderAssumptions = () => {
    if (!Array.isArray(reportJson?.assumptions) || !reportJson.assumptions.length) return null;
    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          10) Risks & Mitigations
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 0 }}>
          {reportJson.assumptions.map((assumption, index) => (
            <Typography component="li" variant="body2" key={`assumption-${index}`} sx={{ lineHeight: 1.7 }}>
              {assumption}
            </Typography>
          ))}
        </Box>
      </Paper>
    );
  };

  const renderSensitivity = () => {
    if (!Array.isArray(reportJson?.sensitivity) || !reportJson.sensitivity.length) return null;
    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Sensitivity Scenarios
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid rgba(224,224,224,1)' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <TableCell>Scenario</TableCell>
                <TableCell align="right">Projected randomizations</TableCell>
                <TableCell align="center">Meets target?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportJson.sensitivity.map((entry, index) => (
                <TableRow key={`sensitivity-${index}`}>
                  <TableCell>{entry.scenario || `Scenario ${index + 1}`}</TableCell>
                  <TableCell align="right">
                    {entry.projected_total_randomizations != null
                      ? entry.projected_total_randomizations
                      : '—'}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, color: entry.meets_target ? 'success.main' : 'error.main' }}>
                    {entry.meets_target ? 'Yes' : 'No'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderCountryTable = () => {
    if (!reportJson?.countries || !reportJson.countries.length) return null;

    return (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          9) Country Recommendation & Portfolio Fit
        </Typography>
        <TableContainer>
          <Table size="small" sx={{ border: '1px solid rgba(224,224,224,1)' }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                <TableCell>Rank</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Composite Score</TableCell>
                <TableCell>Proposed Sites</TableCell>
                <TableCell>Expected rpm</TableCell>
                <TableCell>Contribution to N</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Key Risks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportJson.countries
                .slice()
                .sort((a, b) => (b.composite_score || 0) - (a.composite_score || 0))
                .map((country, index) => {
                  const rpmValue =
                    country.projected_rpm != null
                      ? Number(country.projected_rpm)
                      : country.metrics?.financial?.expected_rpm != null
                      ? Number(country.metrics.financial.expected_rpm)
                      : null;
                  return (
                    <TableRow key={country.country || index}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{country.country}</TableCell>
                      <TableCell>{(country.composite_score || 0).toFixed(1)}</TableCell>
                      <TableCell>{country.proposed_sites || 0}</TableCell>
                      <TableCell>{rpmValue != null && !Number.isNaN(rpmValue) ? rpmValue.toFixed(2) : '—'}</TableCell>
                      <TableCell>{country.projected_contribution_to_N || 0}</TableCell>
                      <TableCell>{country.role || '—'}</TableCell>
                      <TableCell>{country.key_risks || '—'}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };


   // Handle regenerate MeSH terms and refresh all data
  const handleRegenerateMeshTerms = async () => {
    if (!projectId) {
      setRegenerateError('Project ID is required to regenerate MeSH terms');
      return;
    }

    setRegenerateLoading(true);
    setRegenerateError(null);

    try {
      const response = await feasibilityService.regenerateMeshTerms(projectId);
      if (response.success) {
        console.log('✅ MeSH terms regenerated successfully:', response.message);
        // After regenerating, force refresh all data
        await fetchPostgresData(true);
      } else {
        setRegenerateError(response.error || 'Failed to regenerate MeSH terms');
      }
    } catch (error) {
      console.error('Error regenerating MeSH terms:', error);
      setRegenerateError(error.response?.data?.error || error.message || 'Failed to regenerate MeSH terms');
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Fetch PostgreSQL feasibility data (optionally bypassing cache)
  const fetchPostgresData = async (forceRefresh = false) => {
    if (!projectId) {
      setPostgresError('Project ID is required to fetch feasibility data. Please ensure you are viewing a project.');
      setPostgresLoading(false);
      return;
    }

    setPostgresLoading(true);
    setPostgresError(null);
    setPostgresData([]);
    setPostgresSearchTerms([]);

    try {
      const response = await feasibilityService.getPostgresFeasibilityData(projectId, forceRefresh);
        // Handle response structure: { success: true, data: [...], count: ..., diagnosisCondition: ..., countryStudyCounts: ... }
        const data = Array.isArray(response?.data) ? response.data : [];
        setPostgresData(data);
        setPostgresSearchTerms(Array.isArray(response?.searchTermsUsed) ? response.searchTermsUsed : []);
        // Use countryStudyCounts from API if provided, otherwise calculate from data
        if (response?.countryStudyCounts && Array.isArray(response.countryStudyCounts)) {
          setCountryStudyCountsFromApi(response.countryStudyCounts);
        } else {
          setCountryStudyCountsFromApi(null);
        }
        // Reset pagination when new data is loaded
        setPage(0);
        
        // Log the diagnosis condition used for filtering
        if (response?.diagnosisCondition) {
          console.log(`Filtering by protocol diagnosis/condition: "${response.diagnosisCondition}"`);
        }
        if (response?.cached) {
          console.log('✅ Using cached data from MongoDB');
        }

        // Automatically find eligibilities matching Section 5 summary embedding (Queue-based)
        try {
          console.log('🔍 Starting background similarity search job...');
          
          // Get NCT IDs from the freshly loaded data (not from state, which may not be updated yet)
          // Use the `data` variable directly since we just loaded it
          const allowedNctIds = data && data.length > 0
            ? data.map(row => row.nct_id).filter(id => id)
            : [];
          
          // CRITICAL: Only search if we have PostgreSQL data to filter by
          // This prevents searching all 500k+ embeddings when we should only search the filtered set
          if (allowedNctIds.length === 0) {
            console.warn('⚠️ Skipping similarity search: No PostgreSQL data available to filter NCT IDs');
            setSimilarityJobError('Please wait for PostgreSQL data to load before similarity search');
            return;
          }
          
          console.log(`🔍 Starting similarity search (filtered to ${allowedNctIds.length} NCT IDs from PostgreSQL data)`);
          
          // Start background job with filtered NCT IDs
          const jobResponse = await feasibilityService.startSimilaritySearch(projectId, 0.65, 100000, allowedNctIds);
          
          if (jobResponse.success) {
            setSimilarityJobId(jobResponse.jobId);
            setSimilarityJobStatus(jobResponse.status);
            setSimilarityJobProgress(jobResponse.progress || 0);
            setSimilarityJobMessage(jobResponse.progressMessage || jobResponse.message || 'Job started');
            
            console.log(`✅ Similarity search job started: ${jobResponse.jobId} (${jobResponse.status})`);
            
            // If job is already completed (cached result), fetch results immediately
            if (jobResponse.status === 'completed' || jobResponse.cached) {
              const results = await feasibilityService.getSimilaritySearchResults(jobResponse.jobId);
              if (results.success) {
                setEligibilitySearchResults({
                  success: true,
                  count: results.matchedCount,
                  results: results.results,
                  queryText: 'Section 5 Summary (Automatic Match)',
                  automatic: true,
                });
                console.log(`✅ Found ${results.matchedCount} matching studies (from cache)`);
                
                // Warn if we have PostgreSQL data but no similarity matches (likely missing embeddings)
                if (results.matchedCount === 0 && data && data.length > 0) {
                  console.warn(`⚠️ WARNING: Found 0 similarity matches despite ${data.length} filtered NCT IDs in PostgreSQL data`);
                  console.warn(`   This likely means most NCT IDs don't have embeddings generated yet.`);
                  console.warn(`   Check backend logs for embedding coverage percentage.`);
                }
              }
            } else {
              // Start polling for job status
              startSimilarityJobPolling(jobResponse.jobId);
            }
            
            setRequiresEmbeddingGeneration(false);
          } else {
            console.warn('⚠️ Failed to start similarity search job');
            setSimilarityJobError('Failed to start job');
          }
        } catch (matchError) {
          // Check if the error indicates embeddings need to be generated
          const errorData = matchError.response?.data;
          if (errorData?.requiresGeneration) {
            setRequiresEmbeddingGeneration(true);
            console.warn('⚠️ Eligibility embeddings need to be generated:', errorData.error);
          } else {
            // Don't fail the whole request if summary matching fails
            const errorMsg = matchError.response?.data?.error;
            const errorString = typeof errorMsg === 'string' 
              ? errorMsg 
              : (errorMsg?.message || matchError.message || 'Unknown error');
            console.warn('⚠️ Could not start similarity search:', errorString);
            setSimilarityJobError(errorString);
            // This is expected if summary hasn't been processed yet
          }
        }
      } catch (err) {
        let errorMessage = 'Failed to fetch data';
        const errorData = err?.response?.data?.error;
        if (errorData) {
          // Handle both string and object error formats
          errorMessage = typeof errorData === 'string' 
            ? errorData 
            : (errorData?.message || String(errorData));
        } else if (err?.message) {
          errorMessage = String(err.message);
        }
        setPostgresError(errorMessage);
        console.error('Error fetching PostgreSQL data:', err);
      } finally {
        setPostgresLoading(false);
      }
  };

  // Handle PostgreSQL data fetch - load all data on component mount
  useEffect(() => {
    fetchPostgresData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Handle eligibility criteria search
  // Start polling for similarity job status
  const startSimilarityJobPolling = (jobId) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await feasibilityService.getSimilaritySearchStatus(jobId);
        
        setSimilarityJobStatus(status.status);
        setSimilarityJobProgress(status.progress || 0);
        setSimilarityJobMessage(status.progressMessage || '');
        
        console.log(`📊 Job ${jobId} status: ${status.status} (${status.progress}%)`);
        
        if (status.status === 'completed') {
          clearInterval(pollInterval);
          // Fetch results
          const results = await feasibilityService.getSimilaritySearchResults(jobId);
          if (results.success) {
            setEligibilitySearchResults({
              success: true,
              count: results.matchedCount,
              results: results.results,
              queryText: 'Section 5 Summary (Automatic Match)',
              automatic: true,
            });
            console.log(`✅ Found ${results.matchedCount} matching studies`);
            
            // Warn if we have PostgreSQL data but no similarity matches (likely missing embeddings)
            if (results.matchedCount === 0 && postgresData && postgresData.length > 0) {
              console.warn(`⚠️ WARNING: Found 0 similarity matches despite ${postgresData.length} filtered NCT IDs in PostgreSQL data`);
              console.warn(`   This likely means most NCT IDs don't have embeddings generated yet.`);
              console.warn(`   Check backend logs for embedding coverage percentage.`);
              setSimilarityJobError(`No matches found. Only ${postgresData.length > 100 ? 'a small fraction' : 'some'} of the ${postgresData.length} filtered NCT IDs may have embeddings. Check backend logs.`);
            }
          }
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          const errorMsg = status.error;
          const errorString = typeof errorMsg === 'string' 
            ? errorMsg 
            : (errorMsg?.message || 'Job failed');
          setSimilarityJobError(errorString);
          console.error(`❌ Job ${jobId} failed:`, errorString);
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        // Don't clear interval on error, keep trying
      }
    }, 3000); // Poll every 3 seconds
    
    // Stop polling after 30 minutes
    setTimeout(() => clearInterval(pollInterval), 30 * 60 * 1000);
  };

  const handleEligibilitySearch = async () => {
    if (!eligibilitySearchQuery.trim()) {
      setEligibilitySearchError('Please enter a search query');
      return;
    }

    setEligibilitySearchLoading(true);
    setEligibilitySearchError(null);
    setEligibilitySearchResults(null);

    try {
      const results = await feasibilityService.searchSimilarEligibilities(
        eligibilitySearchQuery.trim(),
        50, // limit
        0.7 // minSimilarity
      );

      if (results.success && results.results) {
        setEligibilitySearchResults(results);
        console.log(`✅ Found ${results.count} similar eligibility criteria`);
      } else {
        setEligibilitySearchError('No results found or search failed');
      }
    } catch (error) {
      console.error('Error searching eligibility criteria:', error);
      setEligibilitySearchError(error.response?.data?.error || error.message || 'Failed to search eligibility criteria');
    } finally {
      setEligibilitySearchLoading(false);
    }
  };

  // Handle eligibility embedding generation
  const handleGenerateEmbeddings = async () => {
    setEmbeddingGenerationStatus('generating');
    setEmbeddingGenerationProgress(null);
    setEligibilitySearchError(null);

    try {
      console.log('🚀 Starting eligibility embedding generation...');
      const response = await feasibilityService.generateEligibilityEmbeddings(50, null); // batchSize: 50, limit: null (all records)
      
      if (response.success) {
        setEmbeddingGenerationStatus('success');
        setRequiresEmbeddingGeneration(false);
        console.log('✅ Embedding generation started:', response.message);
        
        // Poll for stats to show progress
        const pollStats = async () => {
          try {
            const stats = await feasibilityService.getEligibilityEmbeddingStats();
            setEmbeddingGenerationProgress({
              total: stats.total || 0,
              withEmbeddings: stats.withEmbeddings || 0,
              percentage: stats.total > 0 ? Math.round((stats.withEmbeddings / stats.total) * 100) : 0,
            });
            
            // If generation is complete, try matching again
            if (stats.withEmbeddings > 0 && stats.withEmbeddings === stats.total) {
              setTimeout(() => {
                // Retry automatic matching
                if (projectId) {
                  feasibilityService.findEligibilitiesMatchingSummary(projectId, 0.65, 100000)
                    .then(matchResponse => {
                      if (matchResponse.success && matchResponse.results) {
                        setEligibilitySearchResults({
                          success: true,
                          count: matchResponse.matchedCount,
                          results: matchResponse.results.map(r => ({
                            nct_id: r.nct_id,
                            similarity: r.similarity,
                            countries: r.countries || [], // Countries from API response
                            criteria: '',
                          })),
                          queryText: 'Section 5 Summary (Automatic Match)',
                          automatic: true,
                        });
                      }
                    })
                    .catch(err => console.warn('Auto-match retry failed:', err));
                }
              }, 2000);
            }
          } catch (err) {
            console.warn('Error polling embedding stats:', err);
          }
        };
        
        // Poll every 5 seconds
        const pollInterval = setInterval(pollStats, 5000);
        pollStats(); // Initial poll
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 300000);
      } else {
        setEmbeddingGenerationStatus('error');
        setEligibilitySearchError('Failed to start embedding generation');
      }
    } catch (error) {
      console.error('Error generating embeddings:', error);
      setEmbeddingGenerationStatus('error');
      setEligibilitySearchError(error.response?.data?.error || error.message || 'Failed to generate embeddings');
    }
  };

  // Calculate country study counts from postgresData (use API data if available, otherwise calculate)
  const countryStudyCounts = useMemo(() => {
    // Use cached data from API if available
    if (countryStudyCountsFromApi && Array.isArray(countryStudyCountsFromApi) && countryStudyCountsFromApi.length > 0) {
      return countryStudyCountsFromApi;
    }

    // Otherwise calculate from postgresData
    if (!postgresData || postgresData.length === 0) return [];

    // Create a map to count NCT IDs per country
    const countryMap = new Map();

    postgresData.forEach((row) => {
      const nctId = row.nct_id;
      const countries = row.countries;

      if (countries && countries !== '—' && countries.trim()) {
        // Split comma-separated countries
        const countryList = countries
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c && c.length > 0);

        // For each country, add this NCT ID to its set
        countryList.forEach((country) => {
          if (!countryMap.has(country)) {
            countryMap.set(country, new Set());
          }
          countryMap.get(country).add(nctId);
        });
      }
    });

    // Convert to array of { country, study_count } and sort by count descending
    const counts = Array.from(countryMap.entries())
      .map(([country, nctIdSet]) => ({
        country,
        study_count: nctIdSet.size,
      }))
      .sort((a, b) => b.study_count - a.study_count);

    return counts;
  }, [postgresData, countryStudyCountsFromApi]);

  // Calculate exact similar counts (country counts for matched NCT IDs from eligibility search)
  const exactSimilarCountsData = useMemo(() => {
    if (!eligibilitySearchResults || !eligibilitySearchResults.results || eligibilitySearchResults.results.length === 0) {
      return null;
    }

    const countryMap = new Map();

    // Use countries from API response (if available) or fall back to postgresData
    eligibilitySearchResults.results.forEach((result) => {
      const nctId = result.nct_id;
      let countries = [];

      // First, try to use countries from the API response (new approach)
      if (result.countries && Array.isArray(result.countries)) {
        countries = result.countries.filter(c => c && c.trim()); // Allow empty arrays (some trials may have no facilities with allowed status)
      } else if (result.countries && typeof result.countries === 'string' && result.countries !== '—' && result.countries.trim()) {
        // Handle string format (comma-separated)
        countries = result.countries
          .split(',')
          .map((c) => c.trim())
          .filter((c) => c && c.length > 0);
      } else {
        // Fallback: try to find in postgresData
        if (postgresData && postgresData.length > 0) {
          const row = postgresData.find(r => r.nct_id === nctId);
          if (row && row.countries && row.countries !== '—' && row.countries.trim()) {
            countries = row.countries
              .split(',')
              .map((c) => c.trim())
              .filter((c) => c && c.length > 0);
          }
        }
      }
      
      // Debug: log if no countries found (only for first few to avoid spam)
      if (countries.length === 0 && eligibilitySearchResults.results.indexOf(result) < 3) {
        console.warn(`⚠️ No countries found for NCT ID: ${nctId}`, {
          hasResultCountries: !!result.countries,
          resultCountriesType: typeof result.countries,
          resultCountries: result.countries,
          foundInPostgresData: postgresData ? !!postgresData.find(r => r.nct_id === nctId) : false,
          postgresDataLength: postgresData ? postgresData.length : 0
        });
      }

      // Add this NCT ID to each country's set
      countries.forEach((country) => {
        if (!countryMap.has(country)) {
          countryMap.set(country, new Set());
        }
        countryMap.get(country).add(nctId);
      });
    });

    const counts = Array.from(countryMap.entries())
      .map(([country, nctIdSet]) => ({
        country,
        exact_similar_count: nctIdSet.size,
      }))
      .sort((a, b) => b.exact_similar_count - a.exact_similar_count);

    return counts;
  }, [eligibilitySearchResults, postgresData]);

  // Merge country study counts with exact similar counts
  const countryStudyCountsWithSimilar = useMemo(() => {
    if (!countryStudyCounts || countryStudyCounts.length === 0) return [];

    if (!exactSimilarCountsData) {
      return countryStudyCounts.map((row) => ({
        ...row,
        exact_similar_count: 0,
      }));
    }

    const similarCountsMap = new Map(exactSimilarCountsData.map((row) => [row.country, row.exact_similar_count]));

    return countryStudyCounts.map((row) => ({
      ...row,
      exact_similar_count: similarCountsMap.get(row.country) || 0,
    }));
  }, [countryStudyCounts, exactSimilarCountsData]);

  // Get color based on count
  const getCountryColor = (count) => {
    if (!count || count === 0) return '#e0e0e0'; // Gray for no data
    if (count >= 100) return '#1a237e'; // Dark blue
    if (count >= 50) return '#283593'; // Medium-dark blue
    if (count >= 20) return '#3949ab'; // Medium blue
    if (count >= 10) return '#5c6bc0'; // Light-medium blue
    if (count >= 5) return '#7986cb'; // Light blue
    return '#9fa8da'; // Very light blue
  };

  // Country name mapping - map TopoJSON names to database names
  const getCountryMapping = () => {
    // This maps TopoJSON country names to what might be in your database
    const mapping = {
      // Americas
      'United States of America': 'United States',
      'USA': 'United States',
      'Canada': 'Canada',
      'Mexico': 'Mexico',
      'Brazil': 'Brazil',
      'Argentina': 'Argentina',
      'Chile': 'Chile',
      'Colombia': 'Colombia',
      'Peru': 'Peru',
      'Venezuela': 'Venezuela',
      
      // Europe
      'United Kingdom': 'United Kingdom',
      'Germany': 'Germany',
      'France': 'France',
      'Italy': 'Italy',
      'Spain': 'Spain',
      'Netherlands': 'Netherlands',
      'Belgium': 'Belgium',
      'Switzerland': 'Switzerland',
      'Austria': 'Austria',
      'Sweden': 'Sweden',
      'Norway': 'Norway',
      'Denmark': 'Denmark',
      'Finland': 'Finland',
      'Poland': 'Poland',
      'Czechia': 'Czech Republic',
      'Czech Republic': 'Czech Republic',
      'Hungary': 'Hungary',
      'Romania': 'Romania',
      'Greece': 'Greece',
      'Portugal': 'Portugal',
      'Ireland': 'Ireland',
      'Russian Federation': 'Russia',
      'Russia': 'Russia',
      'Ukraine': 'Ukraine',
      'Turkey': 'Turkey',
      
      // Asia
      'China': 'China',
      'Japan': 'Japan',
      'Republic of Korea': 'South Korea',
      'South Korea': 'South Korea',
      'Korea': 'South Korea',
      'India': 'India',
      'Thailand': 'Thailand',
      'Vietnam': 'Vietnam',
      'Malaysia': 'Malaysia',
      'Singapore': 'Singapore',
      'Indonesia': 'Indonesia',
      'Philippines': 'Philippines',
      'Taiwan': 'Taiwan',
      'Israel': 'Israel',
      
      // Oceania
      'Australia': 'Australia',
      'New Zealand': 'New Zealand',
      
      // Africa
      'South Africa': 'South Africa',
      'Egypt': 'Egypt',
    };
    
    return mapping;
  };

  // Render world map
  const renderWorldMap = () => {
    if (!countryStudyCountsWithSimilar || countryStudyCountsWithSimilar.length === 0) {
      return null;
    }

    // Create a map of country names to counts (using study_count, not exact_similar_count)
    const countryCountMap = new Map();
    const countryMapping = getCountryMapping();
    
    // First, build a reverse map: TopoJSON name -> database name -> count
    const topoToDbMap = new Map();
    for (const [topoName, dbName] of Object.entries(countryMapping)) {
      const count = countryStudyCountsWithSimilar.find(row => row.country === dbName)?.study_count || 0;
      if (count > 0) {
        topoToDbMap.set(topoName, { dbName, count });
        console.log(`🗺️ Mapped: ${topoName} → ${dbName} (${count} trials)`);
      }
    }
    
    // Also try direct matches
    countryStudyCountsWithSimilar.forEach(row => {
      if (row && row.study_count > 0 && row.country) {
        if (!topoToDbMap.has(row.country)) {
          topoToDbMap.set(row.country, { dbName: row.country, count: row.study_count });
          console.log(`🗺️ Direct match: ${row.country} (${row.study_count} trials)`);
        }
      }
    });
    
    console.log('📊 Total countries with data:', topoToDbMap.size);
    console.log('📋 Countries in database:', countryStudyCountsWithSimilar.map(r => r.country));

    // World map URL (using a simple topojson)
    const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

    return (
      <Box>
        <Box sx={{ position: 'relative', width: '100%', height: '500px', mb: 2 }}>
          <ComposableMap
            projectionConfig={{
              scale: 147,
              center: [0, 20],
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <ZoomableGroup>
              <Geographies geography={geoUrl}>
                {({ geographies }) => {
                  // Debug: Log first 5 country names from TopoJSON
                  if (geographies.length > 0 && !window._mapDebugLogged) {
                    console.log('🌍 Sample TopoJSON country names:', geographies.slice(0, 5).map(g => ({
                      name: g.properties?.name,
                      NAME: g.properties?.NAME,
                      properties: Object.keys(g.properties || {})
                    })));
                    window._mapDebugLogged = true;
                  }
                  
                  return geographies.map((geo) => {
                    // Use lowercase 'name' property (the TopoJSON uses lowercase)
                    const mapCountryName = geo.properties?.name || geo.properties?.NAME || geo.properties?.NAME_LONG || '';
                    if (!mapCountryName) {
                      // Return a default geography if no name
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill="#e0e0e0"
                          stroke="#fff"
                          strokeWidth={0.5}
                          style={{ default: { outline: 'none' } }}
                        />
                      );
                    }
                    
                    // Look up the count using the map
                    let count = 0;
                    let dbCountryName = mapCountryName;
                    
                    const mapData = topoToDbMap.get(mapCountryName);
                    if (mapData) {
                      count = mapData.count;
                      dbCountryName = mapData.dbName;
                      console.log(`✅ Matched: ${mapCountryName} → ${dbCountryName} (${count})`);
                    } else {
                      // Try case-insensitive partial matching
                      for (const [topoName, data] of topoToDbMap.entries()) {
                        if (topoName && mapCountryName) {
                          const mapLower = mapCountryName.toLowerCase();
                          const topoLower = topoName.toLowerCase();
                          if (mapLower === topoLower || mapLower.includes(topoLower) || topoLower.includes(mapLower)) {
                            count = data.count;
                            dbCountryName = data.dbName;
                            console.log(`✅ Partial match: ${mapCountryName} → ${dbCountryName} (${count})`);
                            break;
                          }
                        }
                      }
                    }
                    const fill = getCountryColor(count);
                    const isClickable = count > 0;
                    
                    // Debug specific countries
                    if (count > 0) {
                      console.log(`🎨 Coloring ${mapCountryName}: count=${count}, fill=${fill}`);
                    }

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={fill}
                        stroke="#fff"
                        strokeWidth={0.5}
                        style={{
                          default: {
                            outline: 'none',
                            cursor: isClickable ? 'pointer' : 'default',
                          },
                          hover: {
                            fill: isClickable ? '#1976d2' : fill,
                            outline: 'none',
                            stroke: '#000',
                            strokeWidth: 1,
                          },
                          pressed: {
                            fill: isClickable ? '#1565c0' : fill,
                            outline: 'none',
                          },
                        }}
                        onMouseEnter={(e) => {
                          if (count > 0) {
                            setMapTooltipContent(`${mapCountryName}: ${count} trials`);
                            setMapTooltipPosition({ x: e.clientX, y: e.clientY });
                            setShowMapTooltip(true);
                          }
                        }}
                        onMouseLeave={() => {
                          setShowMapTooltip(false);
                        }}
                        onClick={() => {
                          if (count > 0 && dbCountryName) {
                            // Find the row for this country using the database country name
                            const row = countryStudyCountsWithSimilar.find(r => r.country === dbCountryName);
                            
                            if (row) {
                              // If we have eligibility search results, show those NCT IDs
                              if (eligibilitySearchResults && eligibilitySearchResults.results) {
                                const nctIdsForCountry = eligibilitySearchResults.results
                                  .filter(result => {
                                    let countriesArray = [];
                                    if (Array.isArray(result.countries)) {
                                      countriesArray = result.countries;
                                    } else if (typeof result.countries === 'string' && result.countries) {
                                      countriesArray = result.countries.split(',').map(c => c.trim());
                                    }
                                    return countriesArray.includes(row.country);
                                  })
                                  .sort((a, b) => b.similarity - a.similarity);
                                
                                if (nctIdsForCountry.length > 0) {
                                  setSelectedCountry(row.country);
                                  setSelectedCountryNctIds(nctIdsForCountry);
                                  setNctIdModalOpen(true);
                                }
                              }
                            }
                          }
                        }}
                      />
                    );
                  });
                }}
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
          
          {/* Custom Tooltip */}
          {showMapTooltip && mapTooltipContent && (
            <Box
              sx={{
                position: 'fixed',
                left: mapTooltipPosition.x + 10,
                top: mapTooltipPosition.y - 10,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: 1,
                fontSize: '0.875rem',
                pointerEvents: 'none',
                zIndex: 10000,
                boxShadow: 2,
              }}
            >
              {mapTooltipContent}
            </Box>
          )}
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mr: 1 }}>
            Legend:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#1a237e', border: '1px solid #ccc' }} />
            <Typography variant="caption">≥100</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#283593', border: '1px solid #ccc' }} />
            <Typography variant="caption">50-99</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#3949ab', border: '1px solid #ccc' }} />
            <Typography variant="caption">20-49</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#5c6bc0', border: '1px solid #ccc' }} />
            <Typography variant="caption">10-19</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#7986cb', border: '1px solid #ccc' }} />
            <Typography variant="caption">5-9</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#9fa8da', border: '1px solid #ccc' }} />
            <Typography variant="caption">1-4</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#e0e0e0', border: '1px solid #ccc' }} />
            <Typography variant="caption">0</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render country study counts table
  const renderCountryStudyCountsTable = () => {
    if (postgresLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (postgresError) {
      // Ensure error is always a string (handle error objects)
      const errorMessage = typeof postgresError === 'string' 
        ? postgresError 
        : (postgresError?.message || String(postgresError));
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      );
    }

    if (!countryStudyCountsWithSimilar || countryStudyCountsWithSimilar.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No country data available.
        </Typography>
      );
    }

    return (
      <TableContainer 
        sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          maxHeight: '40vh',
          overflow: 'auto'
        }}
      >
        <Table 
          size="small" 
          sx={{ 
            borderCollapse: 'collapse',
            '& .MuiTableCell-root': {
              border: '1px solid #e0e0e0',
              padding: '6px 10px',
              fontSize: '0.875rem',
            }
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 150, padding: '8px 10px', textAlign: 'center' }}>Country</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 100, textAlign: 'center', padding: '8px 10px' }}>Study Count</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 120, textAlign: 'center', padding: '8px 10px', backgroundColor: eligibilitySearchResults ? '#e3f2fd' : '#f5f5f5' }}>Exact Similar Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {countryStudyCountsWithSimilar.map((row, index) => (
              <TableRow 
                key={row.country || index}
                sx={{
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafafa',
                  },
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  }
                }}
              >
                <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 500, padding: '6px 10px', textAlign: 'center' }}>
                  {row.country}
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 500, padding: '6px 10px' }}>
                  {row.study_count}
                </TableCell>
                <TableCell 
                  sx={{ 
                    border: '1px solid #e0e0e0', 
                    textAlign: 'center', 
                    fontWeight: 500, 
                    padding: '6px 10px', 
                    backgroundColor: eligibilitySearchResults ? '#e3f2fd' : 'transparent',
                    cursor: row.exact_similar_count > 0 ? 'pointer' : 'default',
                    '&:hover': row.exact_similar_count > 0 ? {
                      backgroundColor: '#bbdefb',
                      textDecoration: 'underline'
                    } : {}
                  }}
                  onClick={() => {
                    if (row.exact_similar_count > 0) {
                      // Filter NCT IDs for this country
                      const nctIdsForCountry = eligibilitySearchResults.results
                        .filter(result => {
                          // Handle both string and array formats for countries
                          let countriesArray = [];
                          if (Array.isArray(result.countries)) {
                            countriesArray = result.countries;
                          } else if (typeof result.countries === 'string' && result.countries) {
                            countriesArray = result.countries.split(',').map(c => c.trim());
                          }
                          return countriesArray.includes(row.country);
                        })
                        .sort((a, b) => b.similarity - a.similarity); // Sort by similarity descending
                      
                      setSelectedCountry(row.country);
                      setSelectedCountryNctIds(nctIdsForCountry);
                      setNctIdModalOpen(true);
                    }
                  }}
                >
                  {row.exact_similar_count || 0}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render PostgreSQL data table - shows all NCT IDs from eligibilities table
  const renderPostgresDataTable = () => {
    if (postgresLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (postgresError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {postgresError}
        </Alert>
      );
    }

    if (!postgresData || postgresData.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No data found in eligibilities table.
        </Typography>
      );
    }

    // Helper function to safely convert values to strings
    const safeString = (value) => {
      if (value === null || value === undefined) return '—';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    return (
      <TableContainer 
        sx={{ 
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          maxHeight: '70vh',
          overflow: 'auto'
        }}
      >
        <Table 
          size="small" 
          sx={{ 
            borderCollapse: 'collapse',
            '& .MuiTableCell-root': {
              border: '1px solid #e0e0e0',
              padding: '12px 16px',
            }
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 120 }}>NCT ID</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 400 }}>Criteria</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 150 }}>Countries</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 120 }}>Statuses</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 200 }}>Intervention Names</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 150 }}>Intervention Types</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 150 }}>Agency Classes</TableCell>
              <TableCell sx={{ fontWeight: 600, border: '1px solid #e0e0e0', minWidth: 200 }}>Sponsor Names</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postgresData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
              <TableRow 
                key={row.nct_id || index}
                sx={{
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafafa',
                  },
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  }
                }}
              >
                <TableCell sx={{ border: '1px solid #e0e0e0', fontWeight: 500 }}>
                  {safeString(row.nct_id)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    maxWidth: 400,
                    maxHeight: '200px',
                    overflow: 'auto',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    textAlign: 'left',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                      height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    }
                  }}
                >
                  {safeString(row.criteria)}
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  {safeString(row.countries)}
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  {safeString(row.statuses)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    maxWidth: 200,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {safeString(row.intervention_names)}
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  {safeString(row.intervention_types)}
                </TableCell>
                <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                  {safeString(row.agency_classes)}
                </TableCell>
                <TableCell 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    maxWidth: 250,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {safeString(row.sponsor_names)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={postgresData.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
        />
      </TableContainer>
    );
  };

  // Hardcoded table headers for each table ID (matching the column definitions)
  const getTableHeaders = (tableId) => {
    const headerMap = {
      A1: ['Country', 'ASR /100k', 'Annual incident cases', 'p_subpop', 'p_biomarker+', 'p_tested', 'Consent%', 'Screen-fail%', 'Eligible_y', 'Eligible_m'],
      B1: ['Country', 'Required prior regimens', 'Availability/reimbursed?', '% meeting I/E', 'Alignment', 'Notes'],
      C1: ['Country', 'Biomarker(s)', 'Positivity%', 'Testing coverage%', 'Lab capacity', 'Typical TAT (days)', 'Central-lab viable?', 'Readiness Index', 'Gaps & mitigations'],
      D1: ['Country', 'Start-up USD', 'Fixed/site USD', 'Variable/patient USD', 'Expected rpm', 'CPRP USD', 'Budget share%', 'Cost risks & notes'],
      E1: ['Country', 'Contracting days', 'EC/IRB days', 'Authority days', 'Import/license days', 'Start-up total', 'Median time to FPI', 'Activation risk'],
      F1: ['Country', 'Framework', 'Key requirements', 'Parallelization options', 'Complexity (0-100)', 'Mitigations'],
      G1: ['Country', 'Active competing trials', 'Competition Index', 'Historical median site rpm', 'Historical startup (days)', 'Data quality notes'],
      H1: ['Country', 'DS affiliate?', 'Known site relationships', 'CRO coverage', 'Prior performance notes', 'Leverage opportunities'],
      I1: ['Rank', 'Country', 'Composite Score', 'Proposed sites', 'Expected rpm', 'Projected contribution to N', 'Role', 'Key risks'],
      J1: ['Risk Category', 'Countries affected', 'Description', 'Impact (H/M/L)', 'Likelihood (H/M/L)', 'Mitigation strategy', 'Owner'],
    };
    return headerMap[tableId] || null;
  };

  const getTableHeaderAlignments = (tableId) => {
    const alignmentMap = {
      A1: ['left', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right'],
      B1: ['left', 'left', 'left', 'right', 'center', 'left'],
      C1: ['left', 'left', 'right', 'right', 'left', 'right', 'left', 'right', 'left'],
      D1: ['left', 'right', 'right', 'right', 'right', 'right', 'right', 'left'],
      E1: ['left', 'right', 'right', 'right', 'right', 'right', 'right', 'center'],
      F1: ['left', 'left', 'left', 'left', 'right', 'left'],
      G1: ['left', 'right', 'right', 'right', 'right', 'left'],
      H1: ['left', 'left', 'left', 'left', 'left', 'left'],
      I1: ['right', 'left', 'right', 'right', 'right', 'right', 'left', 'left'],
      J1: ['left', 'left', 'left', 'center', 'center', 'left', 'left'],
    };
    return alignmentMap[tableId] || [];
  };

  const renderFallbackTable = (tableId, title) => {
    const data = fallbackTables[tableId];
    if (!data) return null;

    // Get hardcoded headers for this table
    const hardcodedHeaders = getTableHeaders(tableId);
    const headerAlignments = getTableHeaderAlignments(tableId);

    if (data.type === 'table' && data.rows) {
      // Use hardcoded headers if available, otherwise fall back to extracted headers
      const headers = hardcodedHeaders || data.headers || [];
      // const countryHeaderIndex = headers.findIndex(
      //   (h) => String(h || '').trim().toLowerCase() === 'country'
      // );
      // const filteredRows =
      //   selectedCountries && selectedCountries.length > 0 && countryHeaderIndex >= 0
      //     ? (data.rows || []).filter((row) => {
      //         const countryValue = row?.[countryHeaderIndex];
      //         const key = String(countryValue || '').trim();
      //         return key && selectedCountries.includes(key);
      //       })
      //     : (data.rows || []);
      
      return (
        <Paper key={`fallback-${tableId}`} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {data.title || title}
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ border: '1px solid rgba(224,224,224,1)' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  {headers.map((header, idx) => (
                    <TableCell 
                      key={`${tableId}-header-${idx}`}
                      align={headerAlignments[idx] || 'left'}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map((row, rowIdx) => (
                  <TableRow key={`${tableId}-row-${rowIdx}`}>
                    {row.map((cell, cellIdx) => (
                      <TableCell 
                        key={`${tableId}-cell-${rowIdx}-${cellIdx}`}
                        align={headerAlignments[cellIdx] || 'left'}
                      >
                        {cell || '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      );
    }

    if (data.type === 'text') {
      return (
        <Paper key={`fallback-${tableId}`} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {title}
          </Typography>
          <Box
            component="pre"
            sx={{
              whiteSpace: 'pre-wrap',
              fontSize: 13,
              lineHeight: 1.6,
              backgroundColor: '#fafafa',
              p: 2,
              borderRadius: 1,
              border: '1px solid rgba(224,224,224,0.6)',
            }}
          >
            {data.content}
          </Box>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }} elevation={0}>
      <Typography variant="h6" gutterBottom>
        Country Feasibility Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a country-level feasibility portfolio using protocol data and inclusion/exclusion criteria.
        {useAgenticWorkflow && ' Using advanced agentic workflow with web search, AI generation, and quality evaluation.'}
      </Typography>

      {/* Section 5 Trial Population Summary Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f0f7ff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Section 5 Trial Population Summary
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!projectId) {
                setSummaryError('Project ID is required');
                return;
              }
              setProcessingSummary(true);
              setSummaryError(null);
              try {
                const result = await feasibilityService.processTrialPopulationSummary(projectId);
                setTrialPopulationSummary(result);
                console.log('✅ Section 5 summary processed:', result);
              } catch (err) {
                setSummaryError(err?.response?.data?.error || err?.message || 'Failed to process summary');
                console.error('Error processing summary:', err);
              } finally {
                setProcessingSummary(false);
              }
            }}
            disabled={processingSummary || summaryLoading}
            sx={{ minWidth: 200 }}
          >
            {processingSummary ? 'Processing...' : 'Process Section 5 Summary'}
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Extract disease description, prior treatments, and biomarker status from Section 5. Generate AI summary and vector embeddings for semantic search.
        </Typography>

        {summaryError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {summaryError}
          </Alert>
        )}

        {processingSummary && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Processing Section 5, generating summary and embeddings...
            </Typography>
          </Box>
        )}

        {trialPopulationSummary && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Summary:
              </Typography>
              {!isEditingSummary ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIsEditingSummary(true);
                    setEditedSummary(trialPopulationSummary.summary || '');
                  }}
                  sx={{ minWidth: 80 }}
                >
                  Edit
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={async () => {
                      if (!editedSummary.trim()) {
                        setSummaryError('Summary cannot be empty');
                        return;
                      }
                      setSavingSummary(true);
                      setSummaryError(null);
                      try {
                        const result = await feasibilityService.updateTrialPopulationSummary(projectId, editedSummary);
                        if (result.success) {
                          setTrialPopulationSummary({
                            ...trialPopulationSummary,
                            summary: result.summary,
                            embeddingDimensions: result.embeddingDimensions,
                          });
                          setIsEditingSummary(false);
                          console.log('✅ Summary updated and embeddings recalculated');
                        }
                      } catch (err) {
                        setSummaryError(err?.response?.data?.error || err?.message || 'Failed to save summary');
                      } finally {
                        setSavingSummary(false);
                      }
                    }}
                    disabled={savingSummary || !editedSummary.trim()}
                    startIcon={savingSummary ? <CircularProgress size={16} /> : null}
                    sx={{ minWidth: 100 }}
                  >
                    {savingSummary ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setIsEditingSummary(false);
                      setEditedSummary('');
                      setSummaryError(null);
                    }}
                    disabled={savingSummary}
                    sx={{ minWidth: 80 }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </Box>
            
            {isEditingSummary ? (
              <TextField
                fullWidth
                multiline
                rows={8}
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                variant="outlined"
                sx={{ mb: 2 }}
                placeholder="Enter summary text..."
              />
            ) : (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fafafa' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {trialPopulationSummary.summary}
                </Typography>
              </Paper>
            )}

            {summaryError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {summaryError}
              </Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                  Disease Description:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    wordBreak: 'break-word'
                  }}
                >
                  {trialPopulationSummary.diseaseDescription || 'N/A'}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                  Prior Treatments:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    wordBreak: 'break-word'
                  }}
                >
                  {trialPopulationSummary.priorTreatments || 'N/A'}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                  Biomarker Status:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.875rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    wordBreak: 'break-word'
                  }}
                >
                  {trialPopulationSummary.biomarkerStatus || 'N/A'}
                </Typography>
              </Paper>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Embedding: {trialPopulationSummary.embeddingDimensions || 0} dimensions | Model: {trialPopulationSummary.embeddingModel || 'N/A'}
              {savingSummary && ' | Recalculating embeddings...'}
            </Typography>
          </Box>
        )}

        {/* Load existing summary on mount */}
        {!trialPopulationSummary && !processingSummary && projectId && (
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              setSummaryLoading(true);
              setSummaryError(null);
              try {
                const result = await feasibilityService.getTrialPopulationSummary(projectId);
                setTrialPopulationSummary(result);
              } catch (err) {
                if (err?.response?.status !== 404) {
                  setSummaryError(err?.response?.data?.error || err?.message || 'Failed to load summary');
                }
              } finally {
                setSummaryLoading(false);
              }
            }}
            disabled={summaryLoading}
            sx={{ mt: 1 }}
          >
            {summaryLoading ? 'Loading...' : 'Load Existing Summary'}
          </Button>
        )}
      </Paper>

      {/* PostgreSQL Data Section - Independent from report generation */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Clinical Trials Database (PostgreSQL - ctgov schema)
          </Typography>
          {/* <Button
            variant="outlined"
            size="small"
            onClick={() => fetchPostgresData(true)}
            disabled={postgresLoading}
          >
            {postgresLoading ? 'Reloading...' : 'Reload (ignore cache)'}
          </Button>
        </Box> */}
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleRegenerateMeshTerms}
              disabled={regenerateLoading || postgresLoading}
            >
              {regenerateLoading ? 'Regenerating...' : 'Regenerate MeSH Terms'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchPostgresData(true)}
              disabled={postgresLoading || regenerateLoading}
            >
              {postgresLoading ? 'Reloading...' : 'Reload (ignore cache)'}
            </Button>
          </Box>
        </Box>
        {regenerateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {regenerateError}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          All NCT IDs from eligibilities table with aggregated data from facilities, interventions, and sponsors tables.
        </Typography>
        {postgresSearchTerms.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              MeSH terms used:
            </Typography>
            {postgresSearchTerms.map((term, idx) => (
              <Chip key={`${term}-${idx}`} label={term} size="small" />
            ))}
          </Box>
        )}
        {postgresData.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total: <strong>{postgresData.length}</strong> NCT ID(s) found
          </Typography>
        )}
        {renderPostgresDataTable()}
      </Paper>

      {/* Embedding Generation Section */}
      {requiresEmbeddingGeneration && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem', color: '#856404' }}>
            ⚠️ Eligibility Embeddings Required
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.9rem' }}>
            Eligibility criteria embeddings need to be generated before automatic matching can work. This process may take several minutes depending on the number of records.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              color="warning"
              onClick={handleGenerateEmbeddings}
              disabled={embeddingGenerationStatus === 'generating'}
              startIcon={embeddingGenerationStatus === 'generating' ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {embeddingGenerationStatus === 'generating' ? 'Generating...' : 'Generate Eligibility Embeddings'}
            </Button>
            {embeddingGenerationStatus === 'success' && (
              <Chip label="Generation Started" color="success" size="small" />
            )}
            {embeddingGenerationStatus === 'error' && (
              <Chip label="Generation Failed" color="error" size="small" />
            )}
          </Box>
          {embeddingGenerationProgress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Progress: {embeddingGenerationProgress.withEmbeddings} / {embeddingGenerationProgress.total} embeddings generated ({embeddingGenerationProgress.percentage}%)
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={embeddingGenerationProgress.percentage} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          {embeddingGenerationStatus === 'success' && !embeddingGenerationProgress && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Generation started in the background. Progress will be shown here. This may take several minutes.
            </Typography>
          )}
        </Paper>
      )}

      {/* Eligibility Criteria Search Section - Optional Manual Search (only show if no automatic match) */}
      {(!eligibilitySearchResults || !eligibilitySearchResults.automatic) && !requiresEmbeddingGeneration && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#f9f9f9' }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1.1rem' }}>
            Eligibility Criteria Similarity Search (Optional Manual Search)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
            Manually search for studies with similar eligibility criteria. Note: Automatic matching with Section 5 summary is performed automatically when data loads.
          </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Search Eligibility Criteria"
            placeholder="e.g., patients with breast cancer, age 18-65, no prior treatment"
            value={eligibilitySearchQuery}
            onChange={(e) => setEligibilitySearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && eligibilitySearchQuery.trim()) {
                handleEligibilitySearch();
              }
            }}
            disabled={eligibilitySearchLoading}
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleEligibilitySearch}
            disabled={eligibilitySearchLoading || !eligibilitySearchQuery.trim()}
            sx={{ minWidth: 120 }}
          >
            {eligibilitySearchLoading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
          {eligibilitySearchResults && (
            <Button
              variant="outlined"
              onClick={() => {
                setEligibilitySearchQuery('');
                setEligibilitySearchResults(null);
                setEligibilitySearchError(null);
              }}
            >
              Clear
            </Button>
          )}
        </Box>
        {eligibilitySearchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {eligibilitySearchError}
          </Alert>
        )}
        {eligibilitySearchResults && !eligibilitySearchResults.automatic && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Found <strong>{eligibilitySearchResults.count || 0}</strong> similar studies
              {eligibilitySearchResults.queryText && (
                <> for: "<em>{eligibilitySearchResults.queryText}</em>"</>
              )}
            </Typography>
            {eligibilitySearchResults.results && eligibilitySearchResults.results.length > 0 && (
              <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {eligibilitySearchResults.results.slice(0, 10).map((result, idx) => (
                  <Box key={idx} sx={{ mb: 1, p: 1, backgroundColor: '#fafafa', borderRadius: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {result.nct_id}
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                      (Similarity: {(result.similarity * 100).toFixed(1)}%)
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem', color: 'text.secondary' }}>
                      {result.criteria?.substring(0, 150)}...
                    </Typography>
                  </Box>
                ))}
                {eligibilitySearchResults.results.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ... and {eligibilitySearchResults.results.length - 10} more
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
        </Paper>
      )}

      {/* Similarity Job Progress Display */}
      {(similarityJobStatus === 'queued' || similarityJobStatus === 'processing') && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#fff3e0' }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1.1rem', color: '#f57c00' }}>
            ⏳ Processing Similarity Search
          </Typography>
          <Box sx={{ width: '100%', mb: 1 }}>
            <LinearProgress variant="determinate" value={similarityJobProgress} sx={{ height: 8, borderRadius: 4 }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            <strong>{similarityJobProgress}%</strong> - {similarityJobMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            This may take 5-10 minutes for large datasets. You can close this page and come back later - the job will continue in the background.
          </Typography>
          {similarityJobStatus === 'queued' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }}>
              Job ID: {similarityJobId}
            </Typography>
          )}
        </Paper>
      )}
      
      {/* Similarity Job Error */}
      {similarityJobError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Similarity Search Error:</strong> {typeof similarityJobError === 'string' 
            ? similarityJobError 
            : (similarityJobError?.message || String(similarityJobError))}
        </Alert>
      )}
      
      {/* Automatic Match Results Display */}
      {eligibilitySearchResults && eligibilitySearchResults.automatic && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#e3f2fd' }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem', color: '#1976d2' }}>
            ✅ Automatic Eligibility Match (Section 5 Summary)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
            Found <strong>{eligibilitySearchResults.count || 0}</strong> studies with eligibility criteria matching your Section 5 Trial Population Summary.
            These matches are automatically displayed in the "Exact Similar Count" column below.
          </Typography>
        </Paper>
      )}

      {/* Country Study Counts Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem' }}>
          Country Study Counts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem' }}>
          Number of distinct NCT IDs (studies) associated with each country from the filtered data above.
          {eligibilitySearchResults && (
            <> <strong>Exact Similar Count</strong> shows studies whose eligibility criteria match the Section 5 Trial Population Summary embedding (automatically calculated).</>
          )}
        </Typography>
        {countryStudyCountsWithSimilar.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            Showing <strong>{countryStudyCountsWithSimilar.length}</strong> country/countries
          </Typography>
        )}
        
        {/* World Map Visualization */}
        {countryStudyCountsWithSimilar && countryStudyCountsWithSimilar.length > 0 && (
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              World Map - Country Study Count
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'white' }}>
              {renderWorldMap()}
            </Paper>
          </Box>
        )}
        
        {renderCountryStudyCountsTable()}
      </Paper>

      {/* Report Generation Progress */}
      {workflowStatus === 'running' && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: '#f5f9ff', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CircularProgress size={24} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Generating Feasibility Report
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Analyzing protocol data and gathering real-world insights...
              </Typography>
            </Box>
            <Chip 
              label={`${workflowProgress}%`} 
              color="primary" 
              size="small" 
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={workflowProgress} 
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Paper>
      )}

      {/* Agent Results Summary */}
      {agentResults && workflowStatus === 'completed' && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: '#f0fff4', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'success.dark' }}>
            ✓ Agentic Workflow Completed Successfully
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Search Queries</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {agentResults.metadata?.searchQueries?.total || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tables Generated</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {agentResults.metadata?.generationAgent?.tablesGenerated || 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Quality Score</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {agentResults.evaluation?.overallScore || 'N/A'}/100
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Citations</Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {citationsData.length}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? 'Generating…' : 'Generate Report'}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save Report'}
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        <Chip
          label={useAgenticWorkflow ? "Agentic Workflow: ON" : "Traditional Generation"}
          color={useAgenticWorkflow ? "success" : "default"}
          onClick={() => setUseAgenticWorkflow(!useAgenticWorkflow)}
          sx={{ cursor: 'pointer', fontWeight: 600 }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saveAlert && (
        <Alert severity={saveAlert.severity} sx={{ mb: 2 }}>
          {saveAlert.message}
        </Alert>
      )}

      {renderExecutiveSummary()}
      {renderSummary()}
      
      {/* Only render these summary tables for traditional workflow (they duplicate fallbackTables in agentic workflow) */}
      {!usedFallbackTables && renderCountryRanking()}
      {!usedFallbackTables && renderPortfolioSummary()}
      
      {/* Render tables - Use fallbackTables if available (agentic workflow), otherwise use structured JSON */}
      {usedFallbackTables ? (
        <>
          {/* Agentic Workflow - Render from fallbackTables */}
          {renderFallbackTable('I1', '9) Country Recommendation & Portfolio Fit')}
          {renderFallbackTable('A1', '1) Incidence of the Specific Disease Under Consideration')}
          {renderFallbackTable('B1', '2) Prior Therapy Requirements')}
          {renderFallbackTable('C1', '3) Biomarker Test')}
          {renderFallbackTable('D1', '4) Financial Feasibility')}
          {renderFallbackTable('E1', '5) Timeline for Activation')}
          {renderFallbackTable('F1', '6) Regulatory Strategy')}
          {renderFallbackTable('G1', '7) Competition & Historical Performance (Supportive)')}
          {renderFallbackTable('H1', '8) Sponsor (Daiichi Sankyo) Context')}
          {renderFallbackTable('J1', '10) Risks & Mitigations')}
        </>
      ) : (
        <>
          {/* Traditional Workflow - Render from reportJson */}
          {renderCountryTable()}
          {renderTableA1()}
          {renderTableB1()}
          {renderTableC1()}
          {renderTableD1()}
          {renderTableE1()}
          {renderTableF1()}
          {renderTableG1()}
          {renderTableH1()}
          {renderAssumptions()}
        </>
      )}
      {usedFallbackTables && !useAgenticWorkflow && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Structured JSON was unavailable. Tables above are rendered from the narrative text; values may need manual verification.
        </Alert>
      )}
      {usedFallbackTables && useAgenticWorkflow && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ✓ Tables generated via Agentic Workflow with real-time data search and quality validation.
        </Alert>
      )}
      {renderSensitivity()}

      {/* Country Selection (filters all tables above) */}
      {allGeneratedCountries && allGeneratedCountries.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Country Selection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the countries you want to keep. Unselected countries will be removed from all tables above.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={handleSaveCountrySelection}
              disabled={countrySelectionSaving || !selectedCountries?.length}
              startIcon={countrySelectionSaving ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {countrySelectionSaving ? 'Saving…' : 'Save Selected Countries'}
            </Button>
          </Box>

          {countrySelectionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCountrySelectionError(null)}>
              {countrySelectionError}
            </Alert>
          )}
          {countrySelectionSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setCountrySelectionSuccess(null)}
            >
              {countrySelectionSuccess}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                  <TableCell sx={{ width: 60 }}>Select</TableCell>
                  <TableCell>Country</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allGeneratedCountries.map((name) => (
                  <TableRow key={`country-select-${name}`} hover>
                    <TableCell>
                      <Checkbox
                        checked={selectedCountries.includes(name)}
                        onChange={() => toggleSelectedCountry(name)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      
      {/* Citations and References (Agentic Workflow) */}
      {citationsData && citationsData.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            References & Data Sources
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following sources were used to gather real-world data for this feasibility analysis:
          </Typography>
          <Box component="ol" sx={{ pl: 3 }}>
            {citationsData.map((citation, index) => (
              <Box component="li" key={citation.id || index} sx={{ mb: 2 }}>
                <Typography 
                  component="a" 
                  href={citation.source} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {citation.title || `Source ${index + 1}`}
                </Typography>
                {citation.data?.snippet && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {citation.data.snippet}
                  </Typography>
                )}
                {citation.data?.category && (
                  <Chip 
                    label={citation.data.category} 
                    size="small" 
                    sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      
      {/* Machine-Readable JSON removed - not needed in UI */}

      {/* NCT IDs Modal */}
      <Dialog
        open={nctIdModalOpen}
        onClose={() => setNctIdModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, backgroundColor: '#1976d2', color: 'white' }}>
          NCT IDs for {selectedCountry}
          <IconButton
            aria-label="close"
            onClick={() => setNctIdModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {selectedCountryNctIds.length > 0 ? (
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>NCT ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Similarity Score</TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Countries</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedCountryNctIds.map((result, index) => (
                    <TableRow 
                      key={result.nct_id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f0f0' }
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <a
                          href={`https://clinicaltrials.gov/study/${result.nct_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
                        >
                          {result.nct_id}
                        </a>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${(result.similarity * 100).toFixed(1)}%`}
                          size="small"
                          color={result.similarity >= 0.8 ? 'success' : result.similarity >= 0.7 ? 'primary' : 'default'}
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {result.countries || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No NCT IDs found for this country.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            Total: {selectedCountryNctIds.length} trials
          </Typography>
          <Button onClick={() => setNctIdModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CountryFeasibility;

